import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Modal, Input, Select, Space, Tag, Drawer, Empty, message, Popconfirm, Typography, Switch, Tree, Tooltip } from 'antd';
import {
  PlusOutlined,
  UserAddOutlined,
  SendOutlined,
  LoadingOutlined,
  CloseOutlined,
  ReloadOutlined,
  BookOutlined,
  TeamOutlined,
  RobotOutlined,
  EditOutlined,
  FolderOutlined,
  FileOutlined,
  UploadOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  EyeOutlined,
  ApartmentOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { io, Socket } from 'socket.io-client';
import ReactMarkdown from 'react-markdown';
import './index.css';
import { getWalkingPose, CANVAS_W, CANVAS_H } from './walk-utils';
import remarkGfm from 'remark-gfm';
import { useAuthStore } from '../../stores/authStore';
import api from '../../services/api';
import VoiceInputButton from '../../components/VoiceInputButton';

const { Text, Title } = Typography;
const { TextArea } = Input;

const SPRITE_ASSET_VERSION = '20260609-action-v2';

// Which role gets which sprite sheet (alpha version)
const SPRITE_MAP: Record<string, string> = {
  PM: `/pm_sprite_male_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  PMgr: `/cm_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  Architect: `/jgs_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  UIDesigner: `/uimg_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  Designer: `/uimg_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  Developer: `/yf_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  Frontend: `/yf_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  Backend: `/yf_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  DevOps: `/yf_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  Docs: `/yf_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  Tester: `/cs_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`,
  CM: `/cm_sprite_alpha.png?v=${SPRITE_ASSET_VERSION}`, // product manager uses the new 产品经理 source image
};

const ACTION_SPRITE_VERSION = '20260610-contain-scene-bbox-v3';
const OFFICE_SCENES = [
  { name: '默认办公室', src: '/场景1.png' },
  { name: '晨光办公区', src: '/场景2.png' },
  { name: '用户场景 1', src: '/digital-office-scenes/user_scene_1.png' },
  { name: '用户场景 2', src: '/digital-office-scenes/user_scene_2.png' },
  { name: '用户场景 3', src: '/digital-office-scenes/user_scene_3.png' },
  { name: '用户场景 4', src: '/digital-office-scenes/user_scene_4.png' },
];
const ACTION_SPRITE_MAP: Record<string, string> = {
  // PM
  PM_walk: `/action-sprites/pm_walk.png?v=${ACTION_SPRITE_VERSION}`,
  PM_think: `/action-sprites/pm_think.png?v=${ACTION_SPRITE_VERSION}`,
  PM_work: `/action-sprites/pm_work.png?v=${ACTION_SPRITE_VERSION}`,
  PM_idle: `/action-sprites/pm_idle.png?v=${ACTION_SPRITE_VERSION}`,
  PM_talk: `/action-sprites/pm_talk.png?v=${ACTION_SPRITE_VERSION}`,
  // CM (产品经理) — same action sheets from processed CM videos
  CM_walk: `/action-sprites/cm_walk.png?v=${ACTION_SPRITE_VERSION}`,
  CM_think: `/action-sprites/cm_think.png?v=${ACTION_SPRITE_VERSION}`,
  CM_work: `/action-sprites/cm_work.png?v=${ACTION_SPRITE_VERSION}`,
  CM_idle: `/action-sprites/cm_idle.png?v=${ACTION_SPRITE_VERSION}`,
  CM_talk: `/action-sprites/cm_talk.png?v=${ACTION_SPRITE_VERSION}`,
  // PMgr uses the same CM action sheets
  PMgr_walk: `/action-sprites/cm_walk.png?v=${ACTION_SPRITE_VERSION}`,
  PMgr_think: `/action-sprites/cm_think.png?v=${ACTION_SPRITE_VERSION}`,
  PMgr_work: `/action-sprites/cm_work.png?v=${ACTION_SPRITE_VERSION}`,
  PMgr_idle: `/action-sprites/cm_idle.png?v=${ACTION_SPRITE_VERSION}`,
  PMgr_talk: `/action-sprites/cm_talk.png?v=${ACTION_SPRITE_VERSION}`,
  // 架构师
  jgs_walk: `/action-sprites/jgs_walk.png?v=${ACTION_SPRITE_VERSION}`,
  jgs_think: `/action-sprites/jgs_think.png?v=${ACTION_SPRITE_VERSION}`,
  jgs_work: `/action-sprites/jgs_work.png?v=${ACTION_SPRITE_VERSION}`,
  jgs_idle: `/action-sprites/jgs_idle.png?v=${ACTION_SPRITE_VERSION}`,
  jgs_talk: `/action-sprites/jgs_talk.png?v=${ACTION_SPRITE_VERSION}`,
  Architect_walk: `/action-sprites/jgs_walk.png?v=${ACTION_SPRITE_VERSION}`,
  Architect_think: `/action-sprites/jgs_think.png?v=${ACTION_SPRITE_VERSION}`,
  Architect_work: `/action-sprites/jgs_work.png?v=${ACTION_SPRITE_VERSION}`,
  Architect_idle: `/action-sprites/jgs_idle.png?v=${ACTION_SPRITE_VERSION}`,
  Architect_talk: `/action-sprites/jgs_talk.png?v=${ACTION_SPRITE_VERSION}`,
  // UI美工
  uimg_walk: `/action-sprites/uimg_walk.png?v=${ACTION_SPRITE_VERSION}`,
  uimg_think: `/action-sprites/uimg_think.png?v=${ACTION_SPRITE_VERSION}`,
  uimg_work: `/action-sprites/uimg_work.png?v=${ACTION_SPRITE_VERSION}`,
  uimg_idle: `/action-sprites/uimg_idle.png?v=${ACTION_SPRITE_VERSION}`,
  uimg_talk: `/action-sprites/uimg_talk.png?v=${ACTION_SPRITE_VERSION}`,
  UIDesigner_walk: `/action-sprites/uimg_walk.png?v=${ACTION_SPRITE_VERSION}`,
  UIDesigner_think: `/action-sprites/uimg_think.png?v=${ACTION_SPRITE_VERSION}`,
  UIDesigner_work: `/action-sprites/uimg_work.png?v=${ACTION_SPRITE_VERSION}`,
  UIDesigner_idle: `/action-sprites/uimg_idle.png?v=${ACTION_SPRITE_VERSION}`,
  UIDesigner_talk: `/action-sprites/uimg_talk.png?v=${ACTION_SPRITE_VERSION}`,
  Designer_walk: `/action-sprites/uimg_walk.png?v=${ACTION_SPRITE_VERSION}`,
  Designer_think: `/action-sprites/uimg_think.png?v=${ACTION_SPRITE_VERSION}`,
  Designer_work: `/action-sprites/uimg_work.png?v=${ACTION_SPRITE_VERSION}`,
  Designer_idle: `/action-sprites/uimg_idle.png?v=${ACTION_SPRITE_VERSION}`,
  Designer_talk: `/action-sprites/uimg_talk.png?v=${ACTION_SPRITE_VERSION}`,
  // 研发人员
  yf_walk: `/action-sprites/yf_walk.png?v=${ACTION_SPRITE_VERSION}`,
  yf_think: `/action-sprites/yf_think.png?v=${ACTION_SPRITE_VERSION}`,
  yf_work: `/action-sprites/yf_work.png?v=${ACTION_SPRITE_VERSION}`,
  yf_idle: `/action-sprites/yf_idle.png?v=${ACTION_SPRITE_VERSION}`,
  yf_talk: `/action-sprites/yf_talk.png?v=${ACTION_SPRITE_VERSION}`,
  Developer_walk: `/action-sprites/yf_walk.png?v=${ACTION_SPRITE_VERSION}`,
  Developer_think: `/action-sprites/yf_think.png?v=${ACTION_SPRITE_VERSION}`,
  Developer_work: `/action-sprites/yf_work.png?v=${ACTION_SPRITE_VERSION}`,
  Developer_idle: `/action-sprites/yf_idle.png?v=${ACTION_SPRITE_VERSION}`,
  Developer_talk: `/action-sprites/yf_talk.png?v=${ACTION_SPRITE_VERSION}`,
  Frontend_walk: `/action-sprites/yf_walk.png?v=${ACTION_SPRITE_VERSION}`,
  Frontend_think: `/action-sprites/yf_think.png?v=${ACTION_SPRITE_VERSION}`,
  Frontend_work: `/action-sprites/yf_work.png?v=${ACTION_SPRITE_VERSION}`,
  Frontend_idle: `/action-sprites/yf_idle.png?v=${ACTION_SPRITE_VERSION}`,
  Frontend_talk: `/action-sprites/yf_talk.png?v=${ACTION_SPRITE_VERSION}`,
  Backend_walk: `/action-sprites/yf_walk.png?v=${ACTION_SPRITE_VERSION}`,
  Backend_think: `/action-sprites/yf_think.png?v=${ACTION_SPRITE_VERSION}`,
  Backend_work: `/action-sprites/yf_work.png?v=${ACTION_SPRITE_VERSION}`,
  Backend_idle: `/action-sprites/yf_idle.png?v=${ACTION_SPRITE_VERSION}`,
  Backend_talk: `/action-sprites/yf_talk.png?v=${ACTION_SPRITE_VERSION}`,
  DevOps_walk: `/action-sprites/yf_walk.png?v=${ACTION_SPRITE_VERSION}`,
  DevOps_think: `/action-sprites/yf_think.png?v=${ACTION_SPRITE_VERSION}`,
  DevOps_work: `/action-sprites/yf_work.png?v=${ACTION_SPRITE_VERSION}`,
  DevOps_idle: `/action-sprites/yf_idle.png?v=${ACTION_SPRITE_VERSION}`,
  DevOps_talk: `/action-sprites/yf_talk.png?v=${ACTION_SPRITE_VERSION}`,
  Docs_walk: `/action-sprites/yf_walk.png?v=${ACTION_SPRITE_VERSION}`,
  Docs_think: `/action-sprites/yf_think.png?v=${ACTION_SPRITE_VERSION}`,
  Docs_work: `/action-sprites/yf_work.png?v=${ACTION_SPRITE_VERSION}`,
  Docs_idle: `/action-sprites/yf_idle.png?v=${ACTION_SPRITE_VERSION}`,
  Docs_talk: `/action-sprites/yf_talk.png?v=${ACTION_SPRITE_VERSION}`,
  // 测试工程师
  cs_walk: `/action-sprites/cs_walk.png?v=${ACTION_SPRITE_VERSION}`,
  cs_think: `/action-sprites/cs_think.png?v=${ACTION_SPRITE_VERSION}`,
  cs_work: `/action-sprites/cs_work.png?v=${ACTION_SPRITE_VERSION}`,
  cs_idle: `/action-sprites/cs_idle.png?v=${ACTION_SPRITE_VERSION}`,
  cs_talk: `/action-sprites/cs_talk.png?v=${ACTION_SPRITE_VERSION}`,
  Tester_walk: `/action-sprites/cs_walk.png?v=${ACTION_SPRITE_VERSION}`,
  Tester_think: `/action-sprites/cs_think.png?v=${ACTION_SPRITE_VERSION}`,
  Tester_work: `/action-sprites/cs_work.png?v=${ACTION_SPRITE_VERSION}`,
  Tester_idle: `/action-sprites/cs_idle.png?v=${ACTION_SPRITE_VERSION}`,
  Tester_talk: `/action-sprites/cs_talk.png?v=${ACTION_SPRITE_VERSION}`,
};

type ActorAction = 'walk' | 'think' | 'work' | 'idle' | 'talk';
function resolveActorAction(emp: Employee, moving: boolean, thinkingText?: string, speechContent?: string): ActorAction {
  if (thinkingText || emp.status === 'thinking') return 'think';
  if (speechContent) return 'talk';
  if (emp.status === 'working') return 'work';
  if (moving || emp.status === 'walking') return 'walk';
  return 'idle';
}

// Columns per spritesheet grid (5=15-frame walk, 3=old 9-pose sheet)
const SPRITE_COLS: Record<string, number> = {
  PM: 5, PMgr: 5, CM: 5,
  Architect: 5, UIDesigner: 5, Designer: 5,
  Developer: 5, Frontend: 5, Backend: 5,
  DevOps: 5, Docs: 5, Tester: 5,
};
// Build walk frame sequences from cols x 3 rows grid
const _mkWalk5x3 = (): Array<[number, number]> => {
  const seq: Array<[number, number]> = [];
  for (let r = 0; r < 3; r++) for (let c = 0; c < 5; c++) seq.push([c, r]);
  return seq;
};
const _mkWalk3x3 = (): Array<[number, number]> => {
  const seq: Array<[number, number]> = [];
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) seq.push([c, r]);
  return seq;
};

const spriteBounds = (total: number, cells: number, index: number) => {
  const start = Math.round((index * total) / cells);
  const end = Math.round(((index + 1) * total) / cells);
  return { start, size: Math.max(1, end - start) };
};

const SPRITE_WALK_FRAMES: Record<string, Array<[number, number]>> = {
  // Generated 5×3=15-frame continuous walk cycle
  PM: _mkWalk5x3(),
  PMgr: _mkWalk5x3(),
  CM: _mkWalk5x3(),
  Architect: _mkWalk5x3(),
  UIDesigner: _mkWalk5x3(),
  Designer: _mkWalk5x3(),
  Developer: _mkWalk5x3(),
  Frontend: _mkWalk5x3(),
  Backend: _mkWalk5x3(),
  DevOps: _mkWalk5x3(),
  Docs: _mkWalk5x3(),
  Tester: _mkWalk5x3(),
};

const WALK_FRAME_MS = 150;

const SPRITE_BODY_BOX: Record<string, { x: number; y: number; w: number; h: number }> = {
  // Relative bbox inside one cell, measured from the generated alpha sheets.
  // PM: measured alpha starts near y=16 on action frames; keep extra top head room.
  PM: { x: 92, y: 8, w: 224, h: 376 },
  // CM/PMgr: video-generated 5-column sheet (409×384 cells, chars ~140-170 wide)
  PMgr: { x: 100, y: 20, w: 210, h: 364 },
  CM: { x: 100, y: 20, w: 210, h: 364 },
};

const ROLE_CONFIG: Record<string, { label: string; color: string; hexColor: string; imgPrefix: string; }> = {
  PM: { label: '项目经理', color: '#ff4d4f', hexColor: '#ff7875', imgPrefix: 'pm' },
  CM: { label: '产品经理', color: '#1677ff', hexColor: '#69b1ff', imgPrefix: 'cm' },
  Architect: { label: '架构师', color: '#722ed1', hexColor: '#b37feb', imgPrefix: 'jgs' },
  UIDesigner: { label: 'UI美工', color: '#eb2f96', hexColor: '#ff85c0', imgPrefix: 'uimg' },
  Developer: { label: '研发人员', color: '#52c41a', hexColor: '#95de64', imgPrefix: 'yf' },
  Tester: { label: '测试工程师', color: '#fa8c16', hexColor: '#ffc069', imgPrefix: 'cs' },
  PMgr: { label: '产品经理', color: '#1677ff', hexColor: '#69b1ff', imgPrefix: 'cm' },
  Designer: { label: 'UI美工', color: '#eb2f96', hexColor: '#ff85c0', imgPrefix: 'uimg' },
  Frontend: { label: '研发人员', color: '#52c41a', hexColor: '#95de64', imgPrefix: 'yf' },
  Backend: { label: '研发人员', color: '#fa8c16', hexColor: '#ffc069', imgPrefix: 'yf' },
  DevOps: { label: '研发人员', color: '#13c2c2', hexColor: '#5cdbd3', imgPrefix: 'yf' },
  Docs: { label: '研发人员', color: '#2f54eb', hexColor: '#85a5ff', imgPrefix: 'yf' },
};

const ROLE_RENDER_TUNING: Record<string, { drawH?: number; actionPadBottom?: number }> = {
  PM: { drawH: 174, actionPadBottom: 2 },
  PMgr: { drawH: 176, actionPadBottom: 2 },
  CM: { drawH: 176, actionPadBottom: 2 },
  UIDesigner: { drawH: 157, actionPadBottom: 8 },
  Designer: { drawH: 157, actionPadBottom: 8 },
  Developer: { drawH: 174, actionPadBottom: 3 },
  Frontend: { drawH: 174, actionPadBottom: 3 },
  Backend: { drawH: 174, actionPadBottom: 3 },
  DevOps: { drawH: 174, actionPadBottom: 3 },
  Docs: { drawH: 174, actionPadBottom: 3 },
  Tester: { drawH: 162, actionPadBottom: 8 },
};

const DEFAULT_RENDER_TUNING = { drawH: 162, actionPadBottom: 3 };

const CODE_FENCE_RE = /```[\s\S]*?```/g;

function stripCodeForChat(content: string) {
  if (!content) return '';
  return content
    .replace(CODE_FENCE_RE, '【代码片段已隐藏，详情请查看产出文件】')
    .replace(/`([^`]{30,})`/g, '【代码已隐藏】')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const employeeLabel = (emp?: { name: string; role: string } | null) => {
  if (!emp) return '';
  return `${ROLE_CONFIG[emp.role]?.label || emp.role}-${emp.name}`;
};

function plainChatText(content: string) {
  return stripCodeForChat(content)
    .replace(/[#>*_~`|\[\]()-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function conversationSummary(msg: ConvMsg) {
  const fromLabel = employeeLabel(msg.fromEmployee);
  const toLabel = employeeLabel(msg.toEmployee);
  if (!msg.fromEmployee && msg.toEmployee) return `${toLabel} 收到你的需求`;
  if (msg.fromEmployee && msg.toEmployee) {
    const text = plainChatText(msg.content);
    if (/委派|交给了|安排给|分配给|负责处理|请你完成|你来负责|委任/.test(text)) return `${fromLabel} 委派任务给 ${toLabel}`;
    if (/汇报|报告|完成|产出|交付|结果|总结/.test(text)) return `${fromLabel} 向 ${toLabel} 汇报结果`;
    if (/确认|询问|请问|需要明确|建议/.test(text)) return `${fromLabel} 向 ${toLabel} 确认事项`;
    return `${fromLabel} → ${toLabel} 的沟通`;
  }
  if (msg.fromEmployee) {
    if (msg.fromEmployee.role === 'PM' || msg.fromEmployee.role === 'PMgr') {
      return `${fromLabel} 的回复`;
    }
    return `${fromLabel} 的工作记录`;
  }
  return '系统状态';
}

function conversationBrief(msg: ConvMsg) {
  const text = plainChatText(msg.content);
  if (!text) return '点击查看完整信息';
  // Extract key sentence (first meaningful line)
  const lines = text.split(/[。！？,，\n]/).filter(l => l.trim().length > 6);
  const picked = lines[0] || text;
  return picked.length > 70 ? `${picked.slice(0, 70)}…` : picked;
}

const FLOW_KIND_CONFIG: Record<string, { label: string; tone: string; icon: string }> = {
  conversation: { label: '沟通', tone: 'cyan', icon: '💬' },
  employee: { label: '角色', tone: 'blue', icon: '👤' },
  task: { label: '任务', tone: 'violet', icon: '📌' },
  batch: { label: '派活批次', tone: 'indigo', icon: '🧭' },
  delegate_item: { label: '派活', tone: 'amber', icon: '🤝' },
  confirmation: { label: '确认', tone: 'rose', icon: '❔' },
  session: { label: '干活', tone: 'emerald', icon: '🔨' },
  state_event: { label: '状态', tone: 'slate', icon: '⚡' },
};

function flowKindConfig(kind?: string) {
  return FLOW_KIND_CONFIG[kind || ''] || { label: kind || '记录', tone: 'slate', icon: '•' };
}

function compactFlowTime(value?: string) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function flowStatusLabel(status?: string) {
  if (!status) return '已记录';
  const map: Record<string, string> = {
    anchor: '当前点击', recorded: '已记录', running: '执行中', completed: '已完成', failed: '失败',
    pending: '等待中', all_items_done: '委派已回收', blocked: '阻塞', idle: '空闲', thinking: '思考中',
    working: '工作中', waiting_user: '等用户', error: '异常', info: '信息', warn: '提醒', success: '成功',
  };
  return map[status] || status;
}

function connectedEdgeLabels(nodeId: string, edges: ConversationFlowEdge[]) {
  return edges.filter(e => e.from === nodeId || e.to === nodeId).slice(0, 4).map(e => e.from === nodeId ? `→ ${e.label}` : `← ${e.label}`);
}

function laneLabelOf(lane?: string) {
  const map: Record<string, string> = { user: '用户', PM: '项目经理', PMgr: '产品经理', Architect: '架构师', Developer: '研发人员', Tester: '测试工程师', system: '系统', other: '其他' };
  return map[lane || ''] || lane || '其他';
}

function flowRoleOnly(emp?: { role?: string | null } | null, lane?: string) {
  const role = emp?.role || '';
  return ROLE_CONFIG[role]?.label || laneLabelOf(lane);
}

function flowTitleClean(title: string) {
  return String(title || '')
    .replace(/([\u4e00-\u9fa5A-Za-z]+(?:经理|架构师|美工|人员|工程师|PM|产品|研发|测试|架构))-[^\s>→-]+\s*(?:->|→)\s*([\u4e00-\u9fa5A-Za-z]+(?:经理|架构师|美工|人员|工程师|PM|产品|研发|测试|架构))-[^\s>→-]+/g, '$1 → $2')
    .replace(/([\u4e00-\u9fa5A-Za-z]+(?:经理|架构师|美工|人员|工程师|PM|产品|研发|测试|架构))-[^\s>→-]+/g, '$1')
    .replace(/\s*->\s*/g, ' → ')
    .trim();
}

// 【2026-07-05 问题3】流程卡片标题全部用大白话，弃用"阶段说明/委派执行"等行话
function flowCardMainText(node: ConversationFlowNode) {
  if (node.kind === 'conversation') {
    const from = node.actor ? flowRoleOnly(node.actor, node.lane) : '用户';
    if (!node.target) return `${from}说明当前进展`;
    return `${from}发消息给${flowRoleOnly(node.target)}`;
  }
  if (node.kind === 'delegate_item') {
    const from = node.actor ? flowRoleOnly(node.actor, node.lane) : '用户';
    const to = node.target ? flowRoleOnly(node.target) : '执行人';
    return `${from}给${to}派活`;
  }
  if (node.kind === 'session') {
    const who = node.actor ? flowRoleOnly(node.actor, node.lane) : laneLabelOf(node.lane);
    return `${who}在干活`;
  }
  if (node.kind === 'confirmation') return '需要你拍板';
  return flowTitleClean(node.title);
}

function flowCardSubText(node: ConversationFlowNode) {
  if (node.kind === 'conversation') return node.target ? '工作沟通' : '同步进展，不产生任务';
  if (node.kind === 'delegate_item') return '任务已派发给对方';
  if (node.kind === 'session') return '正在实际执行任务';
  if (node.kind === 'task') return '任务事项';
  if (node.kind === 'batch') return '一批任务同时派发';
  if (node.kind === 'confirmation') return '流程暂停，等你确认后继续';
  return laneLabelOf(node.lane);
}

function flowSummaryClean(summary?: string) {
  const text = plainChatText(summary || '');
  if (!text) return '';
  return text.trim();
}

// 【2026-07-05 问题3】展示层合并（不改后台流程数据）：
// 派活本身就包含沟通，"委派X"卡片吸收紧邻的、同一对人之间的沟通记录
// （含对方的接活回执），避免"委派→沟通→对方沟通回来"三张卡让人看懵。
// 被吸收的记录不丢，点开主卡片在详情里按时间完整展示。
type FlowDisplayGroup = { primary: ConversationFlowNode; merged: ConversationFlowNode[] };
function buildDisplayFlowGroups(nodes: ConversationFlowNode[]): FlowDisplayGroup[] {
  const groups: FlowDisplayGroup[] = [];
  const pairKey = (a?: { role?: string } | null, b?: { role?: string } | null) => `${a?.role || ''}~${b?.role || ''}`;
  for (const node of nodes) {
    const last = groups[groups.length - 1];
    if (node.kind === 'conversation' && node.actor && node.target && last && last.primary.kind === 'delegate_item') {
      const d = last.primary;
      const same = pairKey(node.actor, node.target) === pairKey(d.actor, d.target);
      const reversed = pairKey(node.actor, node.target) === pairKey(d.target, d.actor);
      if (same || reversed) { last.merged.push(node); continue; }
    }
    if (node.kind === 'delegate_item' && last && last.primary.kind === 'conversation' && last.merged.length === 0
        && last.primary.actor && last.primary.target
        && pairKey(last.primary.actor, last.primary.target) === pairKey(node.actor, node.target)) {
      // 沟通在前、委派紧随：以委派为主卡，沟通并入详情
      const conv = last.primary;
      last.primary = node;
      last.merged.push(conv);
      continue;
    }
    groups.push({ primary: node, merged: [] });
  }
  return groups;
}

function flowHasHiddenCode(value?: string) {
  if (!value) return false;
  CODE_FENCE_RE.lastIndex = 0;
  return CODE_FENCE_RE.test(value) || /<details[\s\S]*?<\/details>/i.test(value);
}

function safeRawPreview(raw?: Record<string, any>) {
  if (!raw) return '{}';
  try { return JSON.stringify(raw, null, 2); } catch { return String(raw); }
}

function buildFlowLayout(flow: ConversationFlowData, visibleKinds: string[], highlightPathOnly: boolean) {
  const pathNodes = new Set(flow.path?.nodeIds || []);
  const pathEdges = new Set(flow.path?.edgeIds || []);
  const kinds = new Set(visibleKinds);
  const visibleNodes = (flow.nodes || []).filter(node => kinds.has(node.kind) && (!highlightPathOnly || pathNodes.has(node.id)));
  const visibleIds = new Set(visibleNodes.map(node => node.id));
  const edges = (flow.edges || []).filter(edge => visibleIds.has(edge.from) && visibleIds.has(edge.to) && (!highlightPathOnly || pathEdges.has(edge.id)));
  const lanes = (flow.lanes?.length ? flow.lanes : Array.from(new Set(visibleNodes.map(node => node.lane || 'other'))).map(id => ({ id, label: laneLabelOf(id) })))
    .filter(lane => visibleNodes.some(node => (node.lane || 'other') === lane.id));
  const laneWidth = 220;
  const rowHeight = 116;
  const top = 64;
  const left = 26;
  const positions: Record<string, { x: number; y: number; laneIndex: number; rowIndex: number }> = {};
  const laneCounts: Record<string, number> = {};
  visibleNodes.forEach(node => {
    const laneId = node.lane || 'other';
    const laneIndex = Math.max(0, lanes.findIndex(lane => lane.id === laneId));
    const rowIndex = laneCounts[laneId] || 0;
    laneCounts[laneId] = rowIndex + 1;
    positions[node.id] = { x: left + laneIndex * laneWidth, y: top + rowIndex * rowHeight, laneIndex, rowIndex };
  });
  const maxRows = Math.max(1, ...Object.values(laneCounts));
  return { visibleNodes, edges, lanes, positions, width: Math.max(900, left * 2 + lanes.length * laneWidth), height: top + maxRows * rowHeight + 80 };
}

function findProjectManager(employees: Employee[]) {
  return employees.find(e => e.role === 'PM') || employees.find(e => e.role === 'PMgr') || employees[0] || null;
}

function normalizeThinkingText(rawText: string, emp?: Employee | null) {
  let text = String(rawText || '').trim();
  if (!text) return '';
  const roleLabel = emp ? (ROLE_CONFIG[emp.role]?.label || emp.role) : '';
  const names = [emp?.name, roleLabel, '项目经理助手', '项目经理', '产品经理助手', '产品经理', 'UI美工助手', 'UI美工', '测试工程师助手', '测试工程师'].filter(Boolean) as string[];
  names.forEach(name => {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    text = text
      .replace(new RegExp(`(^|[。！？!?；;，,\\s])${escaped}(正在|需要|认为|判断|准备|会|将|先|已|要|可以|应该)`, 'g'), `$1我$2`)
      .replace(new RegExp(`(^|[。！？!?；;，,\\s])${escaped}[:：]`, 'g'), '$1我：');
  });
  text = text.replace(/我助手/g, '我').replace(/我是我/g, '我是');
  if (/^(正在|需要|认为|判断|准备|先|会|将|要|可以|应该)/.test(text)) text = `我${text}`;
  return text;
}

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  idle: { color: '#52c41a', label: '摸鱼中' }, thinking: { color: '#1677ff', label: '思考中' },
  working: { color: '#fa8c16', label: '搬砖中' }, completed: { color: '#52c41a', label: '完成啦' },
  error: { color: '#ff4d4f', label: '出错了' }, interrupted: { color: '#faad14', label: '已中断' },
  walking: { color: '#13c2c2', label: '溜达中' }, waiting_user: { color: '#faad14', label: '等待中' },
  delegating: { color: '#722ed1', label: '委派中' }, blocked: { color: '#ff4d4f', label: '阻塞中' },
};

interface Employee { id: string; name: string; role: string; status: string; positionX: number; positionY: number; llmConfigId?: string; enableThinking?: boolean; tokenUsed?: number; tokenBudget?: number; personality?: string; training?: any; skillBindings?: EmployeeSkillBinding[]; }
interface EmployeeSkillBinding { id?: string; skillId: string; name?: string; description?: string; source?: string; skillType?: string; enabled?: boolean; priority?: number; localExists?: boolean; }
interface LocalSkill { skillId: string; name: string; description?: string; category?: string; path?: string; exists?: boolean; }

const SKILL_USE_CN: Record<string, string> = {
  'devforge-agent-platform-development': '智坊平台开发规范',
  'devforge-visual-taste': '智坊界面美化规则',
  'frontend-visual-systems': '前端视觉与可读性',
  'subagent-driven-development': '多智能体协作开发',
  'systematic-debugging': '系统化排查问题',
  'test-driven-development': '测试驱动开发',
  'writing-plans': '实施计划拆解',
  'requesting-code-review': '代码提交前审查',
  'github-pr-workflow': 'GitHub 提交流程',
  'github-code-review': 'GitHub 代码评审',
  'prisma-migration-safety-mysql': 'MySQL 数据迁移安全',
  'llm-streaming-sse-transport': 'LLM 流式传输',
  'rag-search-quality-diagnosis': '知识库检索诊断',
  'docx-blind-bid-checker': 'Word 暗标检查',
  'powerpoint': 'PPT 生成与编辑',
  'ocr-and-documents': '文档识别处理',
};
const SKILL_CATEGORY_CN: Record<string, string> = {
  'software-development': '软件开发',
  'devops': '运维部署',
  'github': '代码仓库',
  'productivity': '办公文档',
  'creative': '视觉创作',
  'research': '资料研究',
  'data-science': '数据分析',
  'mlops': '模型工程',
  'media': '媒体处理',
  'note-taking': '知识记录',
};
const skillPurposeLabel = (skill: Partial<LocalSkill & EmployeeSkillBinding & { category?: string }>) => {
  const id = skill.skillId || skill.name || '';
  if (SKILL_USE_CN[id]) return SKILL_USE_CN[id];
  const desc = (skill.description || '').trim();
  if (desc.length > 0) {
    const first = desc.split(/[。.!?；;\n]/)[0].trim();
    if (first.length > 0 && /[\u4e00-\u9fa5]/.test(first)) return first.slice(0, 18);
  }
  const category = (skill as any).category || '';
  if (category && SKILL_CATEGORY_CN[category]) return SKILL_CATEGORY_CN[category];
  return '专业技能';
};
const skillDisplayLabel = (skill: Partial<LocalSkill & EmployeeSkillBinding & { category?: string }>) => `${skillPurposeLabel(skill)}｜${skill.skillId || skill.name || ''}`;
interface WsProject { id: string; name: string; digitalEmployeeCount: number; status: string; }
interface ConvMsg { id: string; content: string; thinking?: string; fromEmployee?: { id: string; name: string; role: string } | null; toEmployee?: { id: string; name: string; role: string } | null; createdAt: string; }
interface ConversationFlowNode { id: string; kind: string; lane?: string; title: string; status?: string; time?: string; summary?: string; rawId?: string; actor?: { name?: string; role?: string } | null; target?: { name?: string; role?: string } | null; raw?: Record<string, any>; }
interface ConversationFlowEdge { id: string; from: string; to: string; label: string; kind?: string; confidence?: string; }
interface ConversationFlowLane { id: string; label: string; }
interface ConversationFlowDiagnostic { id: string; severity: 'high' | 'medium' | 'low' | 'info'; type: string; title: string; detail: string; nodeId?: string; raw?: Record<string, any>; }
interface ConversationFlowData { anchorId: string; projectId?: string; window?: { startAt?: string; endAt?: string; basedOn?: string; nextUserMessageId?: string | null }; lanes?: ConversationFlowLane[]; nodes: ConversationFlowNode[]; edges: ConversationFlowEdge[]; diagnostics?: ConversationFlowDiagnostic[]; path?: { nodeIds?: string[]; edgeIds?: string[] }; raw?: Record<string, any>; }
interface OfficeStateEvent { id: string; event: string; message?: string; level?: string; actorType?: string; fromStatus?: string; toStatus?: string; createdAt: string; employee?: { id: string; name: string; role: string } | null; }
interface PendingConfirmation { id: string; pmEmpId: string; type: string; title?: string; content: string; createdAt: string; pm?: { id: string; name: string; role: string } | null; pmEmployee?: { id: string; name: string; role: string } | null; }

// ====== Canvas 2D drawing functions ======
type OfficeTask = { task: string; status: string; title?: string };

function statusTone(status: string) {
  if (status === 'waiting_user') return { color: '#f59e0b', glow: 'rgba(245,158,11,.34)', label: '等待确认' };
  if (status === 'delegating') return { color: '#7c3aed', glow: 'rgba(124,58,237,.30)', label: '委派中' };
  if (status === 'blocked') return { color: '#dc2626', glow: 'rgba(220,38,38,.30)', label: '阻塞中' };
  if (status === 'thinking') return { color: '#2563eb', glow: 'rgba(37,99,235,.28)', label: '思考中' };
  if (status === 'working') return { color: '#16a34a', glow: 'rgba(22,163,74,.25)', label: '执行中' };
  if (status === 'error') return { color: '#ef4444', glow: 'rgba(239,68,68,.30)', label: '异常' };
  if (status === 'completed') return { color: '#10b981', glow: 'rgba(16,185,129,.25)', label: '已完成' };
  return { color: '#64748b', glow: 'rgba(100,116,139,.16)', label: STATUS_CONFIG[status]?.label || '待命' };
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath(); ctx.moveTo(x+r, y); ctx.lineTo(x+w-r, y); ctx.arcTo(x+w, y, x+w, y+r, r);
  ctx.lineTo(x+w, y+h-r); ctx.arcTo(x+w, y+h, x+w-r, y+h, r); ctx.lineTo(x+r, y+h);
  ctx.arcTo(x, y+h, x, y+h-r, r); ctx.lineTo(x, y+r); ctx.arcTo(x, y, x+r, y, r); ctx.closePath();
}

function drawOfficeScene(ctx: CanvasRenderingContext2D, sceneImg?: HTMLImageElement | null) {
  if (sceneImg && sceneImg.complete && sceneImg.naturalWidth > 0) {
    // Requirement: scene image must be fully visible. If the stage is not enough,
    // pull the camera back (contain), never crop/cover. Use the same image as a
    // blurred extended backdrop so the side/top gaps are not a blue fallback.
    const coverScale = Math.max(CANVAS_W / sceneImg.naturalWidth, CANVAS_H / sceneImg.naturalHeight);
    const coverW = sceneImg.naturalWidth * coverScale;
    const coverH = sceneImg.naturalHeight * coverScale;
    const coverX = (CANVAS_W - coverW) / 2;
    const coverY = (CANVAS_H - coverH) / 2;
    ctx.save();
    ctx.filter = 'blur(18px) brightness(.86) saturate(1.05)';
    ctx.drawImage(sceneImg, coverX - 28, coverY - 28, coverW + 56, coverH + 56);
    ctx.filter = 'none';
    ctx.fillStyle = 'rgba(248,253,255,.12)';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.restore();

    const cameraBack = 0.94;
    const containScale = Math.min(CANVAS_W / sceneImg.naturalWidth, CANVAS_H / sceneImg.naturalHeight) * cameraBack;
    const dw = sceneImg.naturalWidth * containScale;
    const dh = sceneImg.naturalHeight * containScale;
    const dx = (CANVAS_W - dw) / 2;
    const dy = (CANVAS_H - dh) / 2;
    ctx.drawImage(sceneImg, dx, dy, dw, dh);
  } else {
    const g = ctx.createLinearGradient(0, 0, CANVAS_W, CANVAS_H);
    g.addColorStop(0, '#0a1628'); g.addColorStop(.5, '#0f2744'); g.addColorStop(1, '#0a1628');
    ctx.fillStyle = g; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  }
  // Subtle particles (static dots)
  ctx.fillStyle = 'rgba(125,211,252,.12)';
  for (let i=0;i<40;i++) { const x=(i*173+97)%CANVAS_W, y=(i*311+53)%CANVAS_H; ctx.beginPath(); ctx.arc(x,y,1.2,0,Math.PI*2); ctx.fill(); }
}

function getSpeechBubbleStyle(rawText: string, emp: Employee, tone: { color: string }) {
  const text = rawText || '';
  if (/失败|错误|异常|报错|阻塞|卡住|无法|不通过/.test(text)) {
    return { title: '异常提醒', icon: '!', main: '#ef4444', glow: 'rgba(239,68,68,.34)', fillA: 'rgba(255,247,247,.97)', fillB: 'rgba(255,228,230,.92)' };
  }
  if (/\?|？|请确认|需要您|等待|选择|是否|能否/.test(text)) {
    return { title: '需要确认', icon: '?', main: '#f59e0b', glow: 'rgba(245,158,11,.34)', fillA: 'rgba(255,251,235,.97)', fillB: 'rgba(254,243,199,.92)' };
  }
  if (/思考|分析|规划|判断|方案|下一步/.test(text)) {
    return { title: '正在思考', icon: '…', main: '#2563eb', glow: 'rgba(37,99,235,.32)', fillA: 'rgba(239,246,255,.97)', fillB: 'rgba(219,234,254,.92)' };
  }
  return { title: '正在发言', icon: '♪', main: tone.color, glow: tone.color + '40', fillA: 'rgba(248,250,252,.97)', fillB: 'rgba(224,242,254,.92)' };
}

function wrapBubbleLines(ctx: CanvasRenderingContext2D, rawText: string, maxWidth: number, maxLines: number) {
  const source = String(rawText || '').replace(/\s+/g, ' ').replace(/【状态】|【下一步】/g, '').trim();
  const chars = source.split('');
  const lines: string[] = [];
  let cur = '';
  chars.forEach(ch => {
    const test = cur + ch;
    if (ctx.measureText(test).width > maxWidth && cur) {
      lines.push(cur);
      cur = ch;
    } else {
      cur = test;
    }
  });
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    lines.length = maxLines;
    while (ctx.measureText(lines[maxLines - 1] + '…').width > maxWidth && lines[maxLines - 1].length > 0) {
      lines[maxLines - 1] = lines[maxLines - 1].slice(0, -1);
    }
    lines[maxLines - 1] += '…';
  }
  return lines.length ? lines : ['我在处理这件事'];
}

function drawTaskLinks(ctx: CanvasRenderingContext2D, employees: Employee[], taskChain: Array<{fromName:string,toName:string,verb:string,task:string,time:string,ts?:number}>, time: number) {
  // 【2026-07-05 问题5】连线只代表"正在进行中的委派"：
  //   ① 接收方已经不忙（idle/error 等）说明该委派已结束，线必须消失——否则 PM 只委派 1 人
  //     画面却挂着历史委派的 3 条线，用户会误读成"委派了 3 个人"；
  //   ② 同一对 from→to 只保留最新一条，避免重复委派叠线；
  //   ③ 兜底时效 5 分钟，防止状态事件丢失时旧线永远残留。
  const BUSY = new Set(['thinking', 'working', 'delegating', 'review']);
  const nowMs = Date.now();
  const seenPair = new Set<string>();
  const recent = taskChain.slice(-8).reverse().filter(t => {
    if (t.ts && nowMs - t.ts > 5 * 60 * 1000) return false;
    const pair = `${t.fromName}→${t.toName}`;
    if (seenPair.has(pair)) return false;
    seenPair.add(pair);
    const to = employees.find(e => e.name === t.toName);
    return !!to && BUSY.has(to.status);
  }).reverse();
  recent.forEach((t, idx) => {
    const from = employees.find(e => e.name === t.fromName);
    const to = employees.find(e => e.name === t.toName);
    if (!from || !to) return;
    const alpha = Math.max(.18, 1 - (recent.length - idx - 1) * .11);
    ctx.save();
    ctx.strokeStyle = `rgba(14,165,233,${alpha})`; ctx.lineWidth = 2.2; ctx.setLineDash([10, 10]); ctx.lineDashOffset = -time / 42;
    ctx.beginPath();
    const mx = (from.positionX + to.positionX) / 2; const my = Math.min(from.positionY, to.positionY) - 120;
    ctx.moveTo(from.positionX, from.positionY - 76); ctx.quadraticCurveTo(mx, my, to.positionX, to.positionY - 76); ctx.stroke();
    ctx.setLineDash([]);
    const p = ((time / 1600) + idx * .17) % 1;
    const qx = (1-p)*(1-p)*from.positionX + 2*(1-p)*p*mx + p*p*to.positionX;
    const qy = (1-p)*(1-p)*(from.positionY-76) + 2*(1-p)*p*my + p*p*(to.positionY-76);
    ctx.fillStyle = `rgba(14,165,233,${alpha})`; ctx.beginPath(); ctx.arc(qx, qy, 5, 0, Math.PI*2); ctx.fill();
    ctx.restore();
  });
}

function drawCharacter(ctx: CanvasRenderingContext2D, emp: Employee, x: number, y: number, isSelected: boolean, time: number, charImages?: Map<string, HTMLImageElement>, thinkingText?: string, speechContent?: string, taskInfo?: OfficeTask, sprites?: Map<string, HTMLImageElement>, facing = 1, moving = true, actorAction?: ActorAction, actionBboxes?: Map<string, Array<{x:number,y:number,w:number,h:number}>>, talkingWithName?: string) {
  const cfg = ROLE_CONFIG[emp.role] || ROLE_CONFIG.PM;
  const tone = statusTone(emp.status);
  const pulse = (Math.sin(time / 920 + x * .02) + 1) / 2;
  const cx = x, footY = y;
  const img = charImages?.get(emp.role);
  const hasImg = img && img.complete && img.naturalWidth > 0;
  const charH = emp.role === 'PM' ? 118 : 104;
  const charW = emp.role === 'PM' ? 96 : 86;

  // Floor marker: selected employee gets a bright breathing game-style ring.
  ctx.save(); ctx.translate(cx, footY); ctx.scale(1, .36);
  const breathe = (Math.sin(time / 520) + 1) / 2;
  const ringRadius = isSelected ? 72 + breathe * 8 : 58 + pulse * 4;
  const ring = ctx.createRadialGradient(0,0,6,0,0,ringRadius + 18);
  ring.addColorStop(0,isSelected ? 'rgba(255,255,255,.42)' : 'rgba(255,255,255,.22)');
  ring.addColorStop(.34,isSelected ? 'rgba(34,211,238,.42)' : tone.glow);
  ring.addColorStop(.68,isSelected ? 'rgba(59,130,246,.20)' : 'rgba(14,165,233,.10)');
  ring.addColorStop(1,'rgba(14,165,233,0)');
  ctx.fillStyle=ring; ctx.beginPath(); ctx.arc(0,0,ringRadius + 18,0,Math.PI*2); ctx.fill();
  ctx.lineWidth=isSelected?5.8:2.1;
  ctx.strokeStyle=isSelected?'rgba(255,255,255,.98)':tone.color+'cc';
  ctx.shadowColor=isSelected?'rgba(34,211,238,1)':tone.glow;
  ctx.shadowBlur=isSelected?26:6;
  ctx.beginPath(); ctx.arc(0,0,isSelected?57 + breathe * 5:43,0,Math.PI*2); ctx.stroke();
  if (isSelected) {
    ctx.setLineDash([16, 8]); ctx.lineDashOffset = -time / 34;
    ctx.lineWidth = 3.4; ctx.strokeStyle = 'rgba(250,204,21,1)';
    ctx.beginPath(); ctx.arc(0,0,72 + breathe * 7,0,Math.PI*2); ctx.stroke();
    ctx.setLineDash([]);
    ctx.lineWidth = 2.2; ctx.strokeStyle = 'rgba(34,211,238,.92)';
    ctx.beginPath(); ctx.arc(0,0,87 + breathe * 5,0,Math.PI*2); ctx.stroke();
  }
  ctx.restore();
  // Contact shadow
  ctx.save(); ctx.fillStyle='rgba(15,23,42,.20)'; ctx.translate(cx,footY+8); ctx.scale(1,.22); ctx.beginPath(); ctx.arc(0,0,43,0,Math.PI*2); ctx.fill(); ctx.restore();

  let dw=charW, dh=charH;
  const action = actorAction || resolveActorAction(emp, moving, thinkingText, speechContent);
  const actionKey = `${emp.role}_${action}`;
  const actionSprite = sprites?.get(actionKey);
  const baseSprite = sprites?.get(emp.role);
  const sprite = actionSprite && actionSprite.complete && actionSprite.naturalWidth > 0 ? actionSprite : baseSprite;
  const useSprite = sprite && sprite.complete && sprite.naturalWidth > 0;

  if (useSprite) {
    const isActionSheet = !!(actionSprite && actionSprite.complete && actionSprite.naturalWidth > 0);
    const gridCols = isActionSheet ? 5 : (SPRITE_COLS[emp.role] || 3);
    const seq = isActionSheet ? _mkWalk5x3() : (SPRITE_WALK_FRAMES[emp.role] || [[1,1]]);
    const frameSeed = emp.id.split('').reduce((sum, ch, idx) => sum + ch.charCodeAt(0) * (idx + 5), 0);
    const frameMs = WALK_FRAME_MS + (frameSeed % 72) - 24;
    const frame = (moving || isActionSheet) && seq.length>1 ? seq[Math.floor((time + frameSeed * 31) / Math.max(90, frameMs))%seq.length] : seq[0];
    const fc=frame[0], fr=frame[1];
    const bx = spriteBounds(sprite.naturalWidth, gridCols, fc);
    const by = spriteBounds(sprite.naturalHeight, 3, fr);
    const fw = bx.size;
    const fh = by.size;
    const frameIdx = fc + fr * gridCols;
    const measuredBox = isActionSheet ? actionBboxes?.get(actionKey)?.[frameIdx] : undefined;
    // Action sprites are preprocessed into transparent, bottom-aligned cells.  Use the
    // measured alpha bbox for the current frame; the old fixed 52%×86% crop clipped
    // heads and kept gray/dirty edges for non-PM roles.
    const fallbackBox = SPRITE_BODY_BOX[emp.role] || {x:Math.round(fw*.18),y:Math.round(fh*.04),w:Math.round(fw*.64),h:Math.round(fh*.94)};
    const rawBox = (measuredBox && measuredBox.w > 20 && measuredBox.h > 40) ? measuredBox : fallbackBox;
    const tuning = ROLE_RENDER_TUNING[emp.role] || DEFAULT_RENDER_TUNING;
    const padX = isActionSheet ? 8 : 0;
    const padTop = isActionSheet ? 8 : 0;
    const padBottom = isActionSheet ? tuning.actionPadBottom ?? DEFAULT_RENDER_TUNING.actionPadBottom : 0;
    const bodyBox = {
      x: Math.max(0, Math.round(rawBox.x - padX)),
      y: Math.max(0, Math.round(rawBox.y - padTop)),
      w: Math.min(fw - Math.max(0, Math.round(rawBox.x - padX)), Math.round(rawBox.w + padX * 2)),
      h: Math.min(fh - Math.max(0, Math.round(rawBox.y - padTop)), Math.round(rawBox.h + padTop + padBottom)),
    };
    const bodyAR = bodyBox.w / bodyBox.h;
    let sx:number, sy:number, sw:number, sh:number;
    sx = bx.start + bodyBox.x; sy = by.start + bodyBox.y;
    sw = bodyBox.w; sh = bodyBox.h;
    const drawH = tuning.drawH || DEFAULT_RENDER_TUNING.drawH;
    const drawW=drawH*bodyAR;
    dw=drawW; dh=drawH;
    ctx.save(); ctx.imageSmoothingEnabled=true; ctx.imageSmoothingQuality='high';
    ctx.shadowColor='rgba(15,23,42,.24)'; ctx.shadowBlur=7; ctx.shadowOffsetY=6;
    ctx.filter='brightness(0.88) contrast(1.14) saturate(0.96)';
    ctx.translate(cx,footY); ctx.scale(facing<0?-1:1,1);
    ctx.drawImage(sprite,sx,sy,sw,sh,-drawW/2,-drawH,drawW,drawH);
    ctx.filter='none'; ctx.restore();
  } else if (hasImg) {
    const scale = Math.min(charW/img.naturalWidth,charH/img.naturalHeight);
    dw=img.naturalWidth*scale; dh=img.naturalHeight*scale;
    ctx.save(); ctx.shadowColor='rgba(15,23,42,.20)'; ctx.shadowBlur=5; ctx.shadowOffsetY=5;
    ctx.filter='brightness(0.88) contrast(1.08) saturate(0.92)';
    ctx.drawImage(img,cx-dw/2,footY-dh,dw,dh);
    ctx.filter='none'; ctx.restore();
  } else {
    ctx.fillStyle=cfg.hexColor; ctx.beginPath(); ctx.arc(cx,footY-42,28,0,Math.PI*2); ctx.fill();
    ctx.fillStyle='#fff'; ctx.font='800 18px sans-serif'; ctx.textAlign='center'; ctx.fillText(emp.name.charAt(0),cx,footY-35);
  }

  // Name plate
  const label = ROLE_CONFIG[emp.role]?.label || emp.role;
  ctx.font='900 14px Inter, Microsoft YaHei'; const nameW=ctx.measureText(emp.name).width;
  ctx.font='800 12px Inter, Microsoft YaHei'; const labelW=ctx.measureText(label).width;
  const plateW=Math.max(nameW+labelW+72,148), plateH=58;
  const plateX=cx-plateW/2, plateY=footY+28;
  ctx.save(); ctx.shadowColor='rgba(14,165,233,.30)'; ctx.shadowBlur=24; ctx.shadowOffsetY=8;
  const pg=ctx.createLinearGradient(plateX,plateY,plateX+plateW,plateY+plateH);
  pg.addColorStop(0,'rgba(5,16,35,.82)'); pg.addColorStop(.45,'rgba(10,38,68,.76)'); pg.addColorStop(1,'rgba(9,80,92,.72)');
  ctx.fillStyle=pg; ctx.strokeStyle='rgba(125,211,252,.78)'; ctx.lineWidth=1.6;
  roundRect(ctx,plateX,plateY,plateW,plateH,17); ctx.fill(); ctx.stroke();
  ctx.strokeStyle=cfg.color+'99'; ctx.lineWidth=1; roundRect(ctx,plateX+4,plateY+4,plateW-8,plateH-8,14); ctx.stroke();
  ctx.fillStyle=cfg.color; ctx.beginPath(); ctx.arc(plateX+22,plateY+29,10,0,Math.PI*2); ctx.fill();
  ctx.fillStyle='#ecfeff'; ctx.font='900 12px Inter, Microsoft YaHei'; ctx.textAlign='center';
  ctx.fillText(label.slice(0,1),plateX+22,plateY+34);
  ctx.textAlign='left'; ctx.fillStyle='#f8fafc'; ctx.font='900 14px Inter, Microsoft YaHei'; ctx.fillText(emp.name,plateX+44,plateY+22);
  ctx.fillStyle='#7dd3fc'; ctx.font='800 12px Inter, Microsoft YaHei'; ctx.fillText(label,plateX+44,plateY+42);
  ctx.restore();

  // Task/status HUD. No task/no speech/no thinking => draw NOTHING above the character.
  // Speech/thinking content is merged here; never draw a second bubble.
  const thinkingDisplay = thinkingText ? normalizeThinkingText(thinkingText, emp) : '';
  const speechDisplay = speechContent || '';
  const statusLabel = emp.status === 'delegating' ? '委派中'
    : emp.status === 'thinking' ? '思考中'
    : emp.status === 'working' ? '执行中'
    : emp.status === 'waiting_user' ? '等待确认'
    : emp.status === 'blocked' ? '已阻塞'
    : emp.status === 'review' ? '待复核'
    : '';
  const liveText = thinkingDisplay || speechDisplay || taskInfo?.task || (statusLabel ? `当前状态：${statusLabel}` : '');
  if (liveText) {
    const taskTitle = thinkingDisplay ? '思考中' : speechDisplay ? (talkingWithName ? `正在与${talkingWithName}沟通` : '正在沟通') : taskInfo?.title || statusLabel || '当前任务';
    ctx.save();
    ctx.font='900 16px "HarmonyOS Sans SC", "Microsoft YaHei", Inter, sans-serif';
    const preview = liveText.length > 30 ? `${liveText.slice(0,30)}…` : liveText;
    const compact = preview.length <= 10;
    const tagW=Math.min(Math.max(ctx.measureText(preview).width+88,190),280);
    const tagH=compact ? 78 : 118;
    const tagX=cx-tagW/2, tagY=footY-dh-(compact ? 86 : 126);

    // Premium game HUD: dark-blue crystal glass, cyan bevel, gold status rune.
    ctx.shadowColor='rgba(14,165,233,.34)'; ctx.shadowBlur=26; ctx.shadowOffsetY=10;
    const tg=ctx.createLinearGradient(tagX,tagY,tagX+tagW,tagY+tagH);
    tg.addColorStop(0,'rgba(8,20,38,.92)');
    tg.addColorStop(.45,'rgba(7,34,58,.88)');
    tg.addColorStop(1,'rgba(6,54,69,.84)');
    ctx.fillStyle=tg; ctx.strokeStyle='rgba(125,211,252,.78)'; ctx.lineWidth=1.6;
    roundRect(ctx,tagX,tagY,tagW,tagH,16); ctx.fill(); ctx.stroke();

    // CRITICAL: reset ALL shadow props so text doesn't get ghosted
    ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
    ctx.strokeStyle='rgba(255,255,255,.20)'; ctx.lineWidth=1;
    roundRect(ctx,tagX+4,tagY+4,tagW-8,tagH-8,12); ctx.stroke();

    // top highlight and corner ornaments
    const shine = ctx.createLinearGradient(tagX+10, tagY+6, tagX+tagW-10, tagY+6);
    shine.addColorStop(0,'rgba(125,211,252,0)');
    shine.addColorStop(.5,'rgba(224,242,254,.68)');
    shine.addColorStop(1,'rgba(125,211,252,0)');
    ctx.fillStyle=shine; roundRect(ctx, tagX+18, tagY+7, tagW-36, 3, 2); ctx.fill();
    ctx.strokeStyle='rgba(251,191,36,.82)'; ctx.lineWidth=2;
    ctx.beginPath(); ctx.moveTo(tagX+14,tagY+20); ctx.lineTo(tagX+14,tagY+11); ctx.lineTo(tagX+34,tagY+11); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(tagX+tagW-14,tagY+20); ctx.lineTo(tagX+tagW-14,tagY+11); ctx.lineTo(tagX+tagW-34,tagY+11); ctx.stroke();

    // status rune
    const rg=ctx.createRadialGradient(tagX+28,tagY+28,2,tagX+28,tagY+28,16);
    rg.addColorStop(0,'#fff7ed'); rg.addColorStop(.45,'#fbbf24'); rg.addColorStop(1,cfg.color);
    ctx.fillStyle=rg; ctx.beginPath(); ctx.arc(tagX+28,tagY+28,10,0,Math.PI*2); ctx.fill();
    ctx.strokeStyle='rgba(255,255,255,.72)'; ctx.lineWidth=1; ctx.stroke();

    ctx.textAlign='left'; ctx.textBaseline='alphabetic';
    ctx.fillStyle='#e0f2fe'; ctx.font='900 14px "HarmonyOS Sans SC", "Microsoft YaHei", Inter, sans-serif';
    ctx.fillText(taskTitle,tagX+46,tagY+27);
    ctx.fillStyle='#bae6fd'; ctx.font='800 14px "HarmonyOS Sans SC", "Microsoft YaHei", Inter, sans-serif';
    if (compact) {
      ctx.fillText(preview,tagX+22,tagY+56);
    } else {
      const lines = wrapBubbleLines(ctx, liveText, tagW - 44, 3);
      lines.forEach((l, i) => ctx.fillText(l, tagX+22, tagY+56+i*22));
    }
    ctx.restore();
  }


}

export default function DigitalOfficePage() {
  const authUser = useAuthStore(s => s.user);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const charImagesRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const [wsProjects, setWsProjects] = useState<WsProject[]>([]);
  const [currentProject, setCurrentProject] = useState<WsProject | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [conversations, setConversations] = useState<ConvMsg[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ConvMsg | null>(null);
  const [conversationDetailMode, setConversationDetailMode] = useState<'detail' | 'flow'>('detail');
  const [conversationFlow, setConversationFlow] = useState<ConversationFlowData | null>(null);
  const [conversationFlowLoading, setConversationFlowLoading] = useState(false);
  const [conversationFlowError, setConversationFlowError] = useState('');
  const [selectedFlowNodeId, setSelectedFlowNodeId] = useState<string | null>(null);
  const [flowDetailGroup, setFlowDetailGroup] = useState<FlowDisplayGroup | null>(null);
  const flowScrollRef = useRef<HTMLDivElement>(null);
  const flowDragRef = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false });
  const [flowDragActive, setFlowDragActive] = useState(false);
  const [flowKindFilter, setFlowKindFilter] = useState<string[]>(Object.keys(FLOW_KIND_CONFIG));
  const [flowHighlightPathOnly, setFlowHighlightPathOnly] = useState(false);
  const [inputMsg, setInputMsg] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<Employee | null>(null);
  const [localSkills, setLocalSkills] = useState<LocalSkill[]>([]);
  const [employeeSkillDetail, setEmployeeSkillDetail] = useState<any>(null);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState<string | undefined>();
  const [conversationPanelCollapsed, setConversationPanelCollapsed] = useState(() => localStorage.getItem('do-left-chat-collapsed') === '1');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragOff = useRef({ x: 0, y: 0 });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createRole, setCreateRole] = useState('PM');
  const [createName, setCreateName] = useState('');
  const hireRoleOptions = Object.entries(ROLE_CONFIG)
    .filter(([k]) => !['CM','PMgr','Designer','Frontend','Backend','DevOps','Docs'].includes(k) && !employees.some(e => e.role === k))
    .map(([k, v]) => ({ label: v.label, value: k }));
  const openHireModal = () => {
    const firstRole = hireRoleOptions[0]?.value;
    if (!firstRole) {
      message.info('当前项目的可招聘岗位已全部入职');
      return;
    }
    setCreateRole(firstRole);
    setCreateName('');
    setCreateModalOpen(true);
  };
  const [loading, setLoading] = useState(false);
  const [hoveredAgent, setHoveredAgent] = useState<Employee | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const chatEndRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const walkingPoseRef = useRef<Map<string, { x: number; y: number; facing: number; moving?: boolean }>>(new Map());
  const [projectProgress, setProjectProgress] = useState('');
  const [progressMembers, setProgressMembers] = useState<Array<{name:string,role:string,status:string,doing:string,waiting:string,displayTitle?:string,displaySummary?:string}>>([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [trainOpen, setTrainOpen] = useState(false);
  const [trainContent, setTrainContent] = useState('');
  const [thinkingProcess, setThinkingProcess] = useState<Record<string, {empId:string, empName:string, role:string, thinking:string, status:string}>>({});
  const [thinkingDetailOpen, setThinkingDetailOpen] = useState(false);
  const [thinkingDetailEmpId, setThinkingDetailEmpId] = useState<string | null>(null);
  const [taskMap, setTaskMap] = useState<Record<string, { task: string; status: string; title?: string }>>({});
  const [topbarCollapsed, setTopbarCollapsed] = useState(() => {
    try { return localStorage.getItem('do-toolbar-collapsed') === '1'; } catch { return false; }
  });
  const [sceneIndex, setSceneIndex] = useState(() => {
    try {
      const v = localStorage.getItem('do-scene-idx');
      const n = v ? Number(v) : 0;
      return Number.isFinite(n) ? Math.max(0, Math.min(n, OFFICE_SCENES.length - 1)) : 0;
    } catch { return 0; }
  });
  const sceneImageRef = useRef<HTMLImageElement | null>(null);
  const preloadedSceneImagesRef = useRef<Map<number, HTMLImageElement>>(new Map());
  const sceneImage2Ref = useRef<HTMLImageElement | null>(null);
  const spriteImagesRef = useRef<Map<string, HTMLImageElement>>(new Map());
  // Per-frame alpha bboxes for action sprites (indexed by actionKey, 15 frames each)
  const actionBboxRef = useRef<Map<string, Array<{x:number,y:number,w:number,h:number}>>>(new Map());
  const [sceneLoaded, setSceneLoaded] = useState(false);
  const [assetLoading, setAssetLoading] = useState({ done: 0, total: 1, ready: false, label: '准备加载数字办公室资源...' });
  const sceneLoadedRef = useRef(false);
  const sceneLoadTokenRef = useRef(0);
  const [questionPopup, setQuestionPopup] = useState<any>(null);
  const [popupAnswers, setPopupAnswers] = useState<string[]>([]);
  const [suspendedPopup, setSuspendedPopup] = useState<any>(null);
  const [showFileTree, setShowFileTree] = useState(false);
  const [fileTree, setFileTree] = useState<any[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [fileViewer, setFileViewer] = useState<{path:string,content:string}|null>(null);
  const [uploadTargetDir, setUploadTargetDir] = useState('');      // 上传目标目录（工作区内绝对路径），空=根目录
  const [uploading, setUploading] = useState(false);
  const [treeDragOver, setTreeDragOver] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [allResting, setAllResting] = useState(false);
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState<ConvMsg[]>([]);
  const [suggestHire, setSuggestHire] = useState<{roles:string[], empName:string, projectId:string} | null>(null);
  const [needUserInput, setNeedUserInput] = useState<any>(null);
  const [userReply, setUserReply] = useState('');
  const [userReplySubmitting, setUserReplySubmitting] = useState(false);
  const [taskChain, setTaskChain] = useState<Array<{fromName:string, fromRole:string, toName:string, toRole:string, verb:string, task:string, time:string,ts?:number}>>([]);
  const [stateEvents, setStateEvents] = useState<OfficeStateEvent[]>([]);
  const [stateEventsOpen, setStateEventsOpen] = useState(false);
  const [pendingConfirmations, setPendingConfirmations] = useState<PendingConfirmation[]>([]);
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [projectRules, setProjectRules] = useState<any[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const socketRef = useRef<Socket | null>(null);
  const convContainerRef = useRef<HTMLDivElement>(null);
  const speechBubblesRef = useRef<Array<{empId:string;content:string}>>([]);
  const currentProjectRef = useRef<WsProject | null>(null);
  const thinkingRef = useRef<Record<string, {empId:string, empName:string, role:string, thinking:string, status:string}>>({});
  // Keep refs in sync
  useEffect(() => { currentProjectRef.current = currentProject; }, [currentProject]);
  useEffect(() => { thinkingRef.current = thinkingProcess; }, [thinkingProcess]);
  const isCurrentProjectEvent = (payload: any) => {
    const cp = currentProjectRef.current;
    if (!cp) return false;
    if (!payload?.projectId) return false;
    return payload.projectId === cp.id;
  };
  const needUserInputRef = useRef<any>(null);
  const suggestHireRef = useRef<{roles:string[], empName:string, projectId:string} | null>(null);
  const lastReplyAtRef = useRef(0);
  useEffect(() => { needUserInputRef.current = needUserInput; }, [needUserInput]);
  useEffect(() => { suggestHireRef.current = suggestHire; }, [suggestHire]);
  const [progressBoardPos, setProgressBoardPos] = useState(() => {
    try { const s = localStorage.getItem('do-progress-pos'); if (s) return JSON.parse(s); } catch {}
    return { x: 16, y: 92 };
  });
  const progressDragRef = useRef<{startX: number; startY: number; startPX: number; startPY: number} | null>(null);
  const progressBoardRef = useRef<HTMLDivElement>(null);
  const handleProgressMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation(); e.preventDefault();
    progressDragRef.current = { startX: e.clientX, startY: e.clientY, startPX: Number(progressBoardPos.x) || 16, startPY: Number(progressBoardPos.y) || 92 };
    const onMove = (ev: MouseEvent) => {
      const drag = progressDragRef.current;
      if (!drag) return;
      const boardW = progressBoardRef.current?.offsetWidth || 340;
      const boardH = progressBoardRef.current?.offsetHeight || 260;
      const nextX = Math.max(8, Math.min(window.innerWidth - boardW - 8, drag.startPX + ev.clientX - drag.startX));
      const nextY = Math.max(82, Math.min(window.innerHeight - boardH - 92, drag.startPY + ev.clientY - drag.startY));
      setProgressBoardPos({ x: Math.round(nextX), y: Math.round(nextY) });
    };
    const onUp = () => { progressDragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  };
  const saveProgressPos = useCallback(() => { localStorage.setItem('do-progress-pos', JSON.stringify(progressBoardPos)); }, [progressBoardPos]);
  useEffect(() => { saveProgressPos(); }, [progressBoardPos, saveProgressPos]);
  useEffect(() => {
    const boardW = progressBoardRef.current?.offsetWidth || 360;
    const boardH = progressBoardRef.current?.offsetHeight || 260;
    const maxX = Math.max(8, window.innerWidth - boardW - 8);
    const maxY = Math.max(92, window.innerHeight - boardH - 140);
    const x = Number(progressBoardPos.x);
    const y = Number(progressBoardPos.y);
    if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || y < 70 || x > maxX || y > maxY) {
      setProgressBoardPos({ x: 16, y: 92 });
    }
  }, []);

  // Deferred bbox: computes alpha bounding boxes for a sprite sheet lazily.
  // Called from the Phase 2 background effect via requestIdleCallback.
  const computeActionBboxesDeferred = (simg: HTMLImageElement, src: string) => {
    try {
      const firstX = spriteBounds(simg.naturalWidth, 5, 0);
      const firstY = spriteBounds(simg.naturalHeight, 3, 0);
      const off = document.createElement('canvas'); off.width = firstX.size; off.height = firstY.size;
      const octx = off.getContext('2d', { willReadFrequently: true });
      if (!octx) return;
      const frames: Array<{x:number,y:number,w:number,h:number}> = [];
      for (let r = 0; r < 3; r++) {
        const by = spriteBounds(simg.naturalHeight, 3, r);
        for (let c = 0; c < 5; c++) {
          const bx = spriteBounds(simg.naturalWidth, 5, c);
          if (off.width !== bx.size || off.height !== by.size) { off.width = bx.size; off.height = by.size; }
          octx.clearRect(0, 0, bx.size, by.size);
          octx.drawImage(simg, bx.start, by.start, bx.size, by.size, 0, 0, bx.size, by.size);
          const data = octx.getImageData(0, 0, bx.size, by.size).data;
          let minX = bx.size, minY = by.size, maxX = 0, maxY = 0;
          for (let py = 0; py < by.size; py++) {
            for (let px = 0; px < bx.size; px++) {
              if (data[(py*bx.size+px)*4+3] > 24) {
                if (px < minX) minX = px; if (px > maxX) maxX = px;
                if (py < minY) minY = py; if (py > maxY) maxY = py;
              }
            }
          }
          frames.push(maxX >= minX && maxY >= minY ? {x:minX, y:minY, w:Math.max(1,maxX-minX+1), h:Math.max(1,maxY-minY+1)} : {x:0, y:0, w:bx.size, h:by.size});
        }
      }
      Object.entries(ACTION_SPRITE_MAP).forEach(([key, spriteSrc]) => {
        if (spriteSrc === src) actionBboxRef.current.set(key, frames);
      });
    } catch(e) { console.warn('[BBOX] failed to compute bboxes for', src, e); }
  };

  // Two-phase preload: block only on critical assets (current scene + base sprites + idle/walk actions).
  // Other scenes and think/work/talk actions load in background after page reveal.
  // Bbox computation deferred to idle callbacks — no main-thread pixel scan before entry.
  useEffect(() => {
    let cancelled = false;
    const currentSceneAsset = { type: 'scene' as const, key: `scene-${sceneIndex}`, src: OFFICE_SCENES[sceneIndex].src, label: OFFICE_SCENES[sceneIndex].name, index: sceneIndex };
    const baseSprites = Array.from(new Set(Object.values(SPRITE_MAP))).map((src, index) => ({ type: 'base' as const, key: `base-${index}`, src, label: '人物模型' }));
    // Only idle + walk action sprites for blocking phase (think/work/talk load in background)
    const blockingActionSources = new Set<string>();
    Object.entries(ACTION_SPRITE_MAP).forEach(([key, src]) => {
      if (key.endsWith('_idle') || key.endsWith('_walk')) blockingActionSources.add(src);
    });
    const actionSprites = Array.from(blockingActionSources).map((src, index) => ({ type: 'action' as const, key: `action-${index}`, src, label: '基础动作' }));
    const assets = [currentSceneAsset, ...baseSprites, ...actionSprites];
    let done = 0;
    const total = Math.max(1, assets.length);
    setAssetLoading({ done: 0, total, ready: false, label: '正在加载场景与人物模型...' });

    const markDone = (label: string) => {
      done += 1;
      const ready = done >= total && sceneLoadedRef.current;
      if (!cancelled) setAssetLoading({ done, total, ready, label: ready ? '数字办公室资源加载完成' : label });
    };

    const waitImageDecoded = (img: HTMLImageElement) => {
      if (typeof img.decode === 'function') return img.decode().catch(() => undefined);
      return Promise.resolve();
    };

    assets.forEach(asset => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = async () => {
        if (cancelled) return;
        await waitImageDecoded(img);
        if (cancelled) return;
        if (asset.type === 'scene') {
          preloadedSceneImagesRef.current.set(asset.index, img);
          if (asset.index === sceneIndex) {
            sceneImageRef.current = img;
            sceneLoadedRef.current = true;
            setSceneLoaded(true);
          }
        } else if (asset.type === 'base') {
          Object.entries(SPRITE_MAP).forEach(([role, spriteSrc]) => {
            if (spriteSrc === asset.src) spriteImagesRef.current.set(role, img);
          });
        } else {
          Object.entries(ACTION_SPRITE_MAP).forEach(([key, spriteSrc]) => {
            if (spriteSrc === asset.src) spriteImagesRef.current.set(key, img);
          });
          // Compute alpha bbox immediately for blocking idle/walk assets. This keeps
          // the first visible frame from falling back to stale crop boxes that clip heads.
          computeActionBboxesDeferred(img, asset.src);
        }
        markDone(`已加载 ${asset.label}`);
      };
      img.onerror = () => {
        console.warn('[ASSET] load failed', asset.src);
        markDone(`跳过异常资源 ${asset.label}`);
      };
      img.src = asset.src;
      if (img.complete && img.naturalWidth > 0) img.onload?.(new Event('load'));
    });
    return () => { cancelled = true; };
  }, []);

  // Phase 2: lazy-load remaining scenes + action sprites in background after page reveal
  // Also schedules bbox computation via requestIdleCallback so it never blocks the main thread.
  const lazyInitDone = useRef(false);
  useEffect(() => {
    if (lazyInitDone.current) return;
    lazyInitDone.current = true;
    const otherScenes = OFFICE_SCENES.filter((_, i) => i !== sceneIndex);
    const allActionSources = new Set(Object.values(ACTION_SPRITE_MAP));
    const blockingActionSources = new Set<string>();
    Object.entries(ACTION_SPRITE_MAP).forEach(([key, src]) => {
      if (key.endsWith('_idle') || key.endsWith('_walk')) blockingActionSources.add(src);
    });
    const bgActionSources = Array.from(allActionSources).filter(src => !blockingActionSources.has(src));

    const loadBg = (src: string, label: string) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        Object.entries(ACTION_SPRITE_MAP).forEach(([key, spriteSrc]) => {
          if (spriteSrc === src) spriteImagesRef.current.set(key, img);
        });
      };
      img.onerror = () => console.warn('[ASSET-BG] load failed', src);
      img.src = src;
      if (img.complete && img.naturalWidth > 0) img.onload?.(new Event('load'));
    };

    // Load other scenes in background
    otherScenes.forEach(s => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => { preloadedSceneImagesRef.current.set(OFFICE_SCENES.indexOf(s), img); };
      img.onerror = () => console.warn('[ASSET-BG] scene load failed', s.src);
      img.src = s.src;
    });

    // Load other action sprites in background
    bgActionSources.forEach(src => loadBg(src, 'action'));

    // Lazy bbox: schedule per-sprite computation in idle time
    const pendingBbox: Array<{ img: HTMLImageElement; src: string }> = [];
    allActionSources.forEach(src => {
      const cached = spriteImagesRef.current.get(
        Object.entries(ACTION_SPRITE_MAP).find(([, v]) => v === src)?.[0] || ''
      );
      if (cached) pendingBbox.push({ img: cached, src });
    });

    const processOneBbox = () => {
      const job = pendingBbox.shift();
      if (!job) return;
      try {
        computeActionBboxesDeferred(job.img, job.src);
      } catch (e) { /* silent */ }
      if (pendingBbox.length > 0) {
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(processOneBbox, { timeout: 3000 });
        } else {
          setTimeout(processOneBbox, 150);
        }
      }
    };
    if (pendingBbox.length > 0) {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(processOneBbox, { timeout: 2000 });
      } else {
        setTimeout(processOneBbox, 200);
      }
    }
  }, []);

  useEffect(() => {
    const token = ++sceneLoadTokenRef.current;
    const cached = preloadedSceneImagesRef.current.get(sceneIndex);
    if (cached && cached.complete && cached.naturalWidth > 0) {
      sceneImageRef.current = cached;
      sceneLoadedRef.current = true;
      setSceneLoaded(true);
      setAssetLoading(prev => prev.done >= prev.total ? { ...prev, ready: true, label: '数字办公室资源加载完成' } : prev);
      return;
    }
    sceneImageRef.current = null;
    sceneLoadedRef.current = false;
    setSceneLoaded(false);
    setAssetLoading(prev => ({ ...prev, ready: false, label: '正在加载场景背景...' }));
    const scene = OFFICE_SCENES[sceneIndex] || OFFICE_SCENES[0];
    const img = new Image();
    img.decoding = 'async';
    img.onload = async () => {
      if (token !== sceneLoadTokenRef.current) return;
      if (typeof img.decode === 'function') await img.decode().catch(() => undefined);
      if (token !== sceneLoadTokenRef.current) return;
      sceneImageRef.current = img;
      sceneLoadedRef.current = true;
      setSceneLoaded(true);
      preloadedSceneImagesRef.current.set(sceneIndex, img);
      setAssetLoading(prev => prev.done >= prev.total ? { ...prev, ready: true, label: '数字办公室资源加载完成' } : prev);
    };
    img.onerror = () => {
      if (token !== sceneLoadTokenRef.current) return;
      sceneImageRef.current = null;
      sceneLoadedRef.current = false;
      setSceneLoaded(false);
      setAssetLoading(prev => ({ ...prev, ready: false, label: '场景背景加载失败，正在重试...' }));
    };
    img.src = scene.src;
  }, [sceneIndex]);

  // Canvas render loop
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = CANVAS_W * dpr;
    canvas.height = CANVAS_H * dpr;
    const ctx = canvas.getContext('2d', { willReadFrequently: false }); if (!ctx) return;
    ctx.scale(dpr, dpr);
    let t0 = Date.now();
    const render = () => {
      const time = Date.now() - t0;
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      drawOfficeScene(ctx, sceneImageRef.current);
      drawTaskLinks(ctx, employees, taskChain, time);
      const poseCache = new Map<string, { x: number; y: number; facing: number; moving?: boolean }>();
      const basePoses = new Map<string, { x: number; y: number; facing: number; moving?: boolean }>();
      employees.forEach(emp => {
        // 没有任何任务/沟通/思考时不要巡游走路，保持员工自己的默认站立点。
        // 只有非 idle 状态或有实时任务记录时，才保留原来的走动/沟通动效。
        const hasActiveTask = emp.status !== 'idle' || !!taskMap[emp.id];
        basePoses.set(emp.id, hasActiveTask
          ? getWalkingPose(emp, time)
          : { x: emp.positionX, y: emp.positionY, facing: 1, moving: false });
      });
      const nowMs = Date.now();
      const activeTalk = [...taskChain].reverse().find(t => t.ts && nowMs - t.ts < 9000);
      // When activeTalk has a forced bubble, clear speechBubblesRef for that sender
      // to prevent double stacked bubbles (forcedBubble from canvas + stale entry in speechBubblesRef).
      if (activeTalk?.fromName) {
        speechBubblesRef.current = speechBubblesRef.current.filter(b => {
          const emp = employees.find(e => e.id === b.empId);
          return !(emp && emp.name === activeTalk.fromName);
        });
      }
      const poseItems = employees.map(emp => {
        let pose = basePoses.get(emp.id) || { x: emp.positionX, y: emp.positionY, facing: 1, moving: false };
        let forcedAction: ActorAction | undefined;
        let forcedBubble: string | undefined;
        let talkingWithName: string | undefined;
        if (activeTalk && emp.name === activeTalk.fromName) {
          const toEmp = employees.find(e => e.name === activeTalk.toName);
          if (toEmp) {
            const target = { x: toEmp.positionX, y: toEmp.positionY, facing: 1, moving: false };
            const elapsed = nowMs - (activeTalk.ts || nowMs);
            const side = emp.positionX <= target.x ? -1 : 1;
            const dest = { x: target.x + side * 104, y: target.y + 6 };
            const distance = Math.hypot(dest.x - emp.positionX, dest.y - emp.positionY);
            const shouldTeleport = distance > 460;
            if (shouldTeleport && elapsed < 520) {
              pose = { x: emp.positionX, y: emp.positionY, facing: dest.x >= emp.positionX ? 1 : -1, moving: false };
              forcedAction = 'idle';
              forcedBubble = `距离较远，我直接去找 ${activeTalk.toName}`;
            } else if (shouldTeleport || elapsed >= 2600) {
              pose = { x: dest.x, y: dest.y, facing: target.x >= dest.x ? 1 : -1, moving: false };
              forcedAction = 'talk';
              talkingWithName = activeTalk.toName;
              forcedBubble = activeTalk.task ? `${activeTalk.toName}，${activeTalk.task.slice(0, 60)}` : `${activeTalk.toName}，我们同步一下`;
            } else {
              const p = Math.min(1, elapsed / 2600);
              const ease = 1 - Math.pow(1 - p, 3);
              const startX = emp.positionX;
              const startY = emp.positionY;
              pose = { x: startX + (dest.x - startX) * ease, y: startY + (dest.y - startY) * ease, facing: dest.x >= startX ? 1 : -1, moving: true };
              forcedAction = 'walk';
              forcedBubble = `我去找 ${activeTalk.toName} 沟通`;
            }
          }
        } else if (activeTalk && emp.name === activeTalk.toName) {
          const fromEmp = employees.find(e => e.name === activeTalk.fromName);
          if (fromEmp) {
            pose = { x: emp.positionX, y: emp.positionY, facing: fromEmp.positionX >= emp.positionX ? 1 : -1, moving: false };
            forcedAction = 'talk';
            talkingWithName = activeTalk.fromName;
            forcedBubble = activeTalk.task ? `收到，正在听 ${activeTalk.fromName} 同步：${activeTalk.task.slice(0, 48)}` : `正在听 ${activeTalk.fromName} 同步`;
          }
        }
        return { emp, pose, forcedAction, forcedBubble, talkingWithName };
      });
      poseItems.sort((a, b) => (a.pose.y - b.pose.y) || (a.pose.x - b.pose.x) || a.emp.id.localeCompare(b.emp.id))
        .forEach(({ emp, pose, forcedAction, forcedBubble, talkingWithName }) => {
          const bubble = speechBubblesRef.current.find(b => b.empId === emp.id);
          const thinking = thinkingRef.current[emp.id]?.thinking;
          const speechText = forcedBubble || bubble?.content;
          // Diagnostic: detect potential double-bubble scenario
          if (forcedBubble && bubble?.content) {
            console.warn('[DBL-BUBBLE]', emp.name, 'has BOTH forcedBubble:', forcedBubble.slice(0,40),
              'AND speechBubblesRef content:', bubble.content.slice(0,40),
              'activeTalk.ts:', activeTalk?.ts, 'nowMs:', nowMs, 'elapsed:', nowMs - (activeTalk?.ts||0));
          }
          const recentTask = taskChain.filter(t => t.toName === emp.name && (!t.ts || nowMs - t.ts < 45000)).slice(-1)[0];
          const myTask = taskMap[emp.id] || (recentTask && emp.status !== 'idle' ? { task: recentTask.task, status: emp.status } : undefined);
          const action = forcedAction || resolveActorAction(emp, pose.moving || false, thinking, speechText);
          // Keep only real non-walk work/talk/thinking actions anchored to the desk.
          // The idle pause inside the patrol walk loop must stay at the route endpoint;
          // otherwise the character appears to jump back to the middle before turning.
          if (!forcedAction && action !== 'walk' && (thinking || speechText || myTask)) {
            pose = { x: emp.positionX, y: emp.positionY, facing: pose.facing, moving: false };
          }
          // Store the final displayed pose for hit-testing/dragging. Storing the
          // pre-correction walking pose made characters appear to select/drag from
          // an invisible offset position after they stopped.
          poseCache.set(emp.id, pose);
          drawCharacter(ctx, emp, pose.x, pose.y, selectedAgent?.id === emp.id, time, charImagesRef.current, thinking, speechText, myTask, spriteImagesRef.current, pose.facing, pose.moving, action, actionBboxRef.current, talkingWithName);
        });
      walkingPoseRef.current = poseCache;
      animRef.current = requestAnimationFrame(render);
    };
    render();
    return () => cancelAnimationFrame(animRef.current);
  }, [employees, conversations, taskChain, taskMap, sceneIndex, selectedAgent]);

  // Load projects
  useEffect(() => { loadWorkspaceProjects(); loadAvailableModels(); }, []);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !currentProject?.id) return;
    const projectId = currentProject.id;
    socket.emit('join-project', projectId, (ack: any) => {
      if (!ack?.success) console.warn('[DM] WS join-project rejected:', projectId, ack?.message);
      else console.log('[DM] WS joined project:', projectId);
    });
    return () => { socket.emit('leave-project', projectId); };
  }, [currentProject?.id]);

  // 切换项目后清掉上一个项目的上传目标目录，避免上传落入旧工作区路径
  useEffect(() => { setUploadTargetDir(''); }, [currentProject?.id]);

  // 【2026-07-03 新增】状态兜底轮询：每 30 秒从后端拉一次 status-summary / pending-confirmations 对账。
  // WS 事件可能因断线、payload 缺字段等原因丢失，轮询保证界面显示最终与后台流程一致，
  // 并能自动恢复被压制/丢失的确认弹窗（以最小化入口形式出现）。
  useEffect(() => {
    const projectId = currentProject?.id;
    if (!projectId) return;
    const timer = setInterval(() => {
      restoreProjectRuntimeState(projectId).catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [currentProject?.id]);

  // 页面加载/刷新后检测PM是否处于waiting_user状态，恢复确认弹窗
  useEffect(() => {
    // 等待用户确认弹窗以 pending-confirmations 接口为唯一可信来源。
    // 旧版本曾从员工 training.needUserInput 快照恢复弹窗，导致已 answered/cancelled 的历史交付报告
    // 在页面内存或旧快照中被重新弹出；这里仅允许后端仍处于 pending 的 confirmation 恢复。
    if (!needUserInput && Date.now() - lastReplyAtRef.current > 3000) {
      const waitingEmp = employees.find((e: any) => e.status === 'waiting_user' && (e as any).training?.needUserInput);
      const savedInput = waitingEmp ? (waitingEmp as any).training?.needUserInput : null;
      const confirmationId = savedInput?.confirmationId;
      const stillPending = confirmationId && pendingConfirmations.some(item => item.id === confirmationId);
      if (waitingEmp && savedInput && stillPending) {
        const lastHash = (() => { try { return localStorage.getItem('do-last-reply-hash'); } catch { return null; } })();
        const currentQ = String(savedInput.question || '');
        if (lastHash && currentQ.slice(0,120) === lastHash) {
          console.log('[DM] skip recovery: question already replied', lastHash.slice(0,40));
          return;
        }
        setNeedUserInput(savedInput);
      }
    }
  }, [employees, pendingConfirmations]);

  // Mouse handlers — click selects, drag moves, double-click opens drawer
  const clickTimer = useRef(0); const lastClickId = useRef<string | null>(null);
  // 【2026-07-04 问题5】旧逻辑必须按住 Shift 才能拖动（2026-06-11 口径），用户直接拖拽时完全不动、
  // 也没有任何提示，实测被认为"移动不了"。改为：按下即候选拖拽（pendingDrag），移动超过 6px 阈值
  // 自动进入拖拽（无需 Shift；Shift 按下则立即拖拽）；未超过阈值的松手仍是"单击选中"，双击开抽屉不变。
  const pendingDragRef = useRef<{ id: string; dx: number; dy: number; startX: number; startY: number } | null>(null);
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * CANVAS_W / rect.width;
    const my = (e.clientY - rect.top) * CANVAS_H / rect.height;
    for (const emp of [...employees].reverse()) {
      const pose = walkingPoseRef.current.get(emp.id) || { x: emp.positionX, y: emp.positionY, facing: 1, moving: false };
      const dx = mx - pose.x, dy = my - pose.y;
      if (Math.abs(dx) < 58 && Math.abs(dy) < 92) {
        const now = Date.now();
        if (lastClickId.current === emp.id && clickTimer.current && now - clickTimer.current < 400) {
          // Double-click: open drawer; never treat the second click as a drag.
          clickTimer.current = 0; lastClickId.current = null; setDraggingId(null); pendingDragRef.current = null;
          setSelectedAgent(emp); setDrawerOpen(true); return;
        }
        clickTimer.current = now; lastClickId.current = emp.id;
        setSelectedAgent(emp); setDrawerOpen(false);
        if (e.shiftKey) {
          setDraggingId(emp.id); dragOff.current = { x: dx, y: dy }; pendingDragRef.current = null;
        } else {
          setDraggingId(null);
          pendingDragRef.current = { id: emp.id, dx, dy, startX: mx, startY: my };
        }
        return;
      }
    }
    setSelectedAgent(null); setDrawerOpen(false); lastClickId.current = null; pendingDragRef.current = null;
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * CANVAS_W / rect.width;
    const my = (e.clientY - rect.top) * CANVAS_H / rect.height;
    // 移动超过阈值 → 把候选拖拽升级为真实拖拽（左键仍按着才算）
    const pending = pendingDragRef.current;
    if (!draggingId && pending && (e.buttons & 1) && Math.hypot(mx - pending.startX, my - pending.startY) > 6) {
      setDraggingId(pending.id); dragOff.current = { x: pending.dx, y: pending.dy };
      pendingDragRef.current = null;
      setEmployees(prev => prev.map(emp => emp.id === pending.id ? { ...emp, positionX: Math.round(mx - pending.dx), positionY: Math.round(my - pending.dy) } : emp));
      return;
    }
    if (draggingId) {
      setEmployees(prev => prev.map(emp => emp.id === draggingId ? { ...emp, positionX: Math.round(mx - dragOff.current.x), positionY: Math.round(my - dragOff.current.y) } : emp));
    } else {
      const h = employees.find(emp => {
        const pose = walkingPoseRef.current.get(emp.id) || { x: emp.positionX, y: emp.positionY, facing: 1, moving: false };
        return Math.abs(mx - pose.x) < 58 && Math.abs(my - pose.y) < 92;
      });
      setHoveredAgent(h || null); if (h) setTooltipPos({ x: e.clientX, y: e.clientY });
    }
  };
  const handleMouseUp = () => {
    pendingDragRef.current = null;
    if (draggingId) {
      const emp = employees.find(e => e.id === draggingId);
      // 拖拽结束必须持久化到后端；失败时提示用户（旧代码静默吞错，刷新后位置回跳，看起来就是"移动不了"）
      if (emp) api.put(`/digital-employees/employees/${emp.id}/position`, { positionX: emp.positionX, positionY: emp.positionY }).catch(() => { message.warning('位置保存失败，刷新后可能回到原位'); });
      setDraggingId(null);
    }
  };

  // API handlers
  const loadWorkspaceProjects = async () => { try { const { data } = await api.get('/digital-employees/workspace-projects'); setWsProjects(data); } catch {} };
  const restoreProjectRuntimeState = async (projectId: string, options?: { openConfirmation?: boolean }) => {
    try {
      const [confirmRes, eventRes, summaryRes] = await Promise.all([
        api.get(`/digital-employees/projects/${projectId}/pending-confirmations`),
        api.get(`/digital-employees/projects/${projectId}/state-events?limit=30`),
        api.get(`/digital-employees/projects/${projectId}/status-summary`),
      ]);
      const confirmations: PendingConfirmation[] = confirmRes.data || [];
      const events: OfficeStateEvent[] = eventRes.data || [];
      setPendingConfirmations(confirmations);
      setStateEvents(events);
      // 【2026-07-04 修复】对账关闭：当前打开的弹窗如果已不在后端 pending 列表里
      // （在别的标签页被回答了、或被新确认替代），必须自动关闭，而不是永远挂着。
      const curInput = needUserInputRef.current;
      if (curInput) {
        const staleById = curInput.confirmationId && !confirmations.some(c => c.id === curInput.confirmationId);
        const staleNoId = !curInput.confirmationId && confirmations.length === 0;
        if (staleById || staleNoId) {
          console.log('[DM] close stale confirmation modal (answered elsewhere or superseded)');
          setNeedUserInput(null);
          setUserReply('');
        }
      }
      const summary = summaryRes.data || {};
      setProgressMembers(Array.isArray(summary.members) ? summary.members : []);
      const statusMembers = Array.isArray(summary.members) ? summary.members : [];
      const allIdle = statusMembers.length > 0 && statusMembers.every((m: any) => !m?.status || m.status === 'idle');
      if (allIdle) {
        setTaskMap({});
        speechBubblesRef.current = [];
        thinkingRef.current = {};
        setThinkingProcess({});
      }
      if (statusMembers.length > 0) {
        setTaskMap(prev => {
          const next = { ...prev };
          statusMembers.forEach((m: any) => {
            if (!m?.empId || !m.status || m.status === 'idle') {
              if (m?.empId) delete next[m.empId];
              return;
            }
            next[m.empId] = {
              task: m.displaySummary || m.displayTitle || (m.status === 'delegating' ? '委派中'
                : m.status === 'thinking' ? '思考中'
                : m.status === 'working' ? '执行中'
                : m.status === 'waiting_user' ? '等待确认'
                : m.status === 'blocked' ? '已阻塞'
                : m.status === 'review' ? '待复核'
                : `当前状态：${m.status}`),
              title: m.displayTitle,
              status: m.status,
            };
          });
          return next;
        });
      }
      if ((!needUserInputRef.current || options?.openConfirmation) && confirmations.length > 0) {
        const first = confirmations[0];
        // 【2026-07-04 问题4】optionsJson 可能是对象（{ accessUrls }，交付确认的真实访问地址），
        // 旧代码把它当字符串塞进 options，会被渲染成 "[object Object]"。
        const firstOptionsJson: any = (first as any).optionsJson;
        const accessUrls = firstOptionsJson && typeof firstOptionsJson === 'object' && Array.isArray(firstOptionsJson.accessUrls)
          ? firstOptionsJson.accessUrls
          : (Array.isArray((first as any).accessUrls) ? (first as any).accessUrls : []);
        setNeedUserInput({
          confirmationId: first.id,
          empId: first.pmEmpId,
          empName: first.pm?.name || first.pmEmployee?.name || '项目经理',
          role: '项目经理',
          projectId,
          title: first.title || '项目经理需要你确认',
          question: first.content,
          questions: Array.isArray((first as any).questions) ? (first as any).questions : undefined,
          summary: (first as any).summary || '',
          options: typeof firstOptionsJson === 'string' ? firstOptionsJson : ((first as any).options || ''),
          accessUrls,
          type: first.type || 'pm_user_confirmation',
          createdAt: first.createdAt,
          _minimized: !options?.openConfirmation,
        });
      }
    } catch (e) {
      console.warn('[DM] restore runtime state failed:', e);
    }
  };
  const selectProject = async (p: WsProject) => {
    setCurrentProject(p);
    try {
      const [eRes, cRes] = await Promise.all([api.get(`/digital-employees/employees?projectId=${p.id}`), api.get(`/digital-employees/projects/${p.id}/conversations?limit=50`)]);
      let loadedEmployees: Employee[] = eRes.data || [];
      setConversations(cRes.data || []);
      if (loadedEmployees.length === 0) {
        try {
          const pm = await api.post('/digital-employees/employees', { projectId: p.id, role: 'PM', positionX: CANVAS_W / 2, positionY: CANVAS_H / 2 });
          loadedEmployees = [pm.data];
        } catch {}
      } else if (loadedEmployees.length === 1 && loadedEmployees[0]?.role === 'PM' && loadedEmployees[0].positionX === 0 && loadedEmployees[0].positionY === 0) {
        // Backfill old auto-created PMs that only have the database default (0,0).
        // Real dragged positions are persisted through updatePosition and must not be overwritten.
        const centeredPm = { ...loadedEmployees[0], positionX: CANVAS_W / 2, positionY: CANVAS_H / 2 };
        loadedEmployees = [centeredPm];
        try { await api.put(`/digital-employees/employees/${centeredPm.id}/position`, { positionX: centeredPm.positionX, positionY: centeredPm.positionY }); } catch {}
      }
      setEmployees(loadedEmployees);
      // 加载项目规则
      try { const rRes = await api.get(`/digital-employees/projects/${p.id}/rules`); setProjectRules(rRes.data || []); } catch {}
      await restoreProjectRuntimeState(p.id);
      setTimeout(() => evaluateProgress(), 500);
    } catch {}
  };
  const hireEmployee = async () => {
    if (!currentProject) return;
    if (!hireRoleOptions.some(option => option.value === createRole)) {
      message.error('请选择可招聘岗位');
      return;
    }
    setLoading(true);
    try { const { data } = await api.post('/digital-employees/employees', { projectId: currentProject.id, role: createRole, name: createName || undefined }); setEmployees(prev => [...prev, data]); setCreateModalOpen(false); setCreateName(''); message.success(`${data.name} 入职成功！`); }
    catch (e: any) { message.error(e.response?.data?.message || '招聘失败'); }
    setLoading(false);
  };
  const sendMessage = async () => {
    const toEmp = findProjectManager(employees);
    if (!inputMsg.trim() || !currentProject || !toEmp) {
      console.log('[DM] send blocked:', { hasInput: !!inputMsg.trim(), hasProject: !!currentProject, target: toEmp?.name });
      return;
    }
    const msg = inputMsg;
    setInputMsg('');
    console.log('[DM] sending to', toEmp.name, ':', msg.slice(0,50));
    speechBubblesRef.current = speechBubblesRef.current.filter(x => x.empId !== toEmp.id);
    const optimisticMsg: ConvMsg = { id: 'opt-' + Date.now(), content: msg, fromEmployee: null, toEmployee: { id: toEmp.id, name: toEmp.name, role: toEmp.role }, createdAt: new Date().toISOString() };
    setConversations(prev => [...prev, optimisticMsg]);
    try {
      const res = await api.post(`/digital-employees/projects/${currentProject.id}/messages`, { content: msg, toEmpId: toEmp.id });
      console.log('[DM] sent OK:', res.status);
    } catch (e) {
      console.error('[DM] send FAIL:', e);
      message.error('发送失败');
    }
  };
  const evaluateProgress = async () => { if (!currentProject) return; setProgressLoading(true); try { const r = await api.get(`/digital-employees/projects/${currentProject.id}/status-summary`); setProjectProgress(r.data?.displayText || r.data?.summary || ''); setProgressMembers(r.data?.members || []); } catch {} finally { setProgressLoading(false); } };

  const loadFileTree = async () => { if (!currentProject) return; setTreeLoading(true); try { const { data } = await api.get(`/digital-employees/projects/${currentProject.id}/files`); setFileTree(data||[]); setShowFileTree(true); } catch { message.error('文件列表加载失败'); } finally { setTreeLoading(false); } };

  /** 上传文件到当前工作区（uploadTargetDir 为空时传到根目录），重名由后端自动改名不覆盖 */
  const uploadWorkspaceFiles = async (rawFiles: File[]) => {
    if (!currentProject || !rawFiles.length || uploading) return;
    const MAX = 100 * 1024 * 1024;
    const oversize = rawFiles.filter(f => f.size > MAX);
    if (oversize.length) message.warning(`已跳过超过100MB的文件：${oversize.map(f => f.name).join('、')}`);
    const files = rawFiles.filter(f => f.size <= MAX).slice(0, 20);
    if (rawFiles.length > 20) message.warning('单次最多上传20个文件，多余的已跳过');
    if (!files.length) return;
    const key = 'office-upload';
    setUploading(true);
    message.open({ key, type: 'loading', content: `正在上传 ${files.length} 个文件...`, duration: 0 });
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      if (uploadTargetDir) fd.append('targetPath', uploadTargetDir);
      const { data } = await api.post(`/digital-employees/projects/${currentProject.id}/files/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: e => { if (e.total) message.open({ key, type: 'loading', content: `正在上传 ${files.length} 个文件... ${Math.round((e.loaded / e.total) * 100)}%`, duration: 0 }); },
      });
      if (data?.error) { message.open({ key, type: 'error', content: data.error, duration: 4 }); return; }
      const results = data?.results || [];
      const okList = results.filter((r: any) => r.ok);
      const failList = results.filter((r: any) => !r.ok);
      const renamedCount = okList.filter((r: any) => r.renamed).length;
      if (failList.length) {
        message.open({ key, type: 'warning', content: `成功 ${okList.length} 个，失败 ${failList.length} 个：${failList.map((r: any) => r.name).join('、')}`, duration: 6 });
      } else {
        message.open({ key, type: 'success', content: `已上传 ${okList.length} 个文件${renamedCount ? `（${renamedCount} 个重名已自动改名）` : ''}`, duration: 3 });
      }
      if (okList.length) { try { const { data: tree } = await api.get(`/digital-employees/projects/${currentProject.id}/files`); setFileTree(tree || []); } catch {} }
    } catch (e: any) {
      message.open({ key, type: 'error', content: e?.response?.data?.message || '上传失败，请重试', duration: 4 });
    } finally {
      setUploading(false);
    }
  };

  /** 将后端文件树转为Ant Design Tree需要的格式 */
  const toTreeData = (nodes: any[]): any[] => nodes.map(n => ({
    title: n.name,
    key: n.path,
    icon: n.type === 'directory' ? <FolderOutlined style={{ color: '#faad14' }} /> : <FileOutlined style={{ color: '#999' }} />,
    children: n.children ? toTreeData(n.children) : undefined,
    isLeaf: n.type === 'file',
    path: n.path,
  }));
  const loadAvailableModels = async () => { try { const { data } = await api.get('/llm/models-with-provider'); setAvailableModels(data || []); } catch {} };
  const loadLocalSkills = async () => {
    try { const { data } = await api.get('/digital-employees/skills/local'); setLocalSkills(data || []); }
    catch { message.error('技能库加载失败'); }
  };
  const loadEmployeeSkills = async (agentId: string) => {
    setSkillsLoading(true);
    try { const { data } = await api.get(`/digital-employees/employees/${agentId}/skills`); setEmployeeSkillDetail(data); }
    catch { message.error('员工技能加载失败'); }
    finally { setSkillsLoading(false); }
  };
  const attachSkillToAgent = async () => {
    if (!selectedAgent || !selectedSkillId) return;
    const skill = localSkills.find(s => s.skillId === selectedSkillId);
    try {
      await api.post(`/digital-employees/employees/${selectedAgent.id}/skills/attach`, {
        skillId: selectedSkillId,
        name: skill?.name || selectedSkillId,
        description: skill?.description || '',
        source: 'local-hermes',
        skillType: 'hermes-skill',
        enabled: true,
      });
      message.success('技能已加载到数字人');
      setSelectedSkillId(undefined);
      await loadEmployeeSkills(selectedAgent.id);
      const { data } = await api.get(`/digital-employees/employees?projectId=${currentProject?.id || ''}`);
      setEmployees(data || []);
    } catch (e: any) { message.error(e.response?.data?.message || '技能加载失败'); }
  };
  const detachSkillFromAgent = async (skillId: string) => {
    if (!selectedAgent) return;
    try {
      await api.post(`/digital-employees/employees/${selectedAgent.id}/skills/detach`, { skillId });
      message.success('技能已停用');
      await loadEmployeeSkills(selectedAgent.id);
      const { data } = await api.get(`/digital-employees/employees?projectId=${currentProject?.id || ''}`);
      setEmployees(data || []);
    } catch { message.error('技能停用失败'); }
  };

  useEffect(() => {
    if (drawerOpen && selectedAgent) {
      loadLocalSkills();
      loadEmployeeSkills(selectedAgent.id);
    }
  }, [drawerOpen, selectedAgent?.id]);

  // WebSocket — connect once, use ref for currentProject to avoid stale closure
  useEffect(() => {
    const token = useAuthStore.getState().token; if (!token || socketRef.current) return;
    const socket = io('/ws', { auth: { token } }); socketRef.current = socket;
    socket.on('connect', () => {
      console.log('[DM] WS connected, id:', socket.id);
      const projectId = currentProjectRef.current?.id;
      if (projectId) {
        socket.emit('join-project', projectId, (ack: any) => {
          if (!ack?.success) console.warn('[DM] WS reconnect join-project rejected:', projectId, ack?.message);
          else console.log('[DM] WS reconnect joined project:', projectId);
        });
        restoreProjectRuntimeState(projectId).catch(e => console.warn('[DM] restore after WS connect failed:', e));
      }
    });
    socket.on('disconnect', (reason: string) => console.log('[DM] WS disconnected:', reason));
    socket.on('office:message', (msg: ConvMsg) => {
      const cp = currentProjectRef.current;
      console.log('[DM] WS office:message, cp:', cp?.id?.slice(0,8), 'msg.projectId:', (msg as any).projectId?.slice(0,8), 'match:', cp && (msg as any).projectId === cp.id);
      if (cp && (msg as any).projectId === cp.id) {
        console.log('[DM] WS message MATCH, adding to conv');
        setConversations(prev => {
          // 【2026-07-03】按 id 去重：WS 重连/重复推送时同一条消息不能插两次
          if ((msg as any).id && prev.some(m => m.id === (msg as any).id)) return prev;
          // 去重：用户消息会被乐观更新+WS回显两次，过滤重复
          if (!msg.fromEmployee) {
            const dup = prev.find(m => !m.fromEmployee && m.content === msg.content);
            if (dup) return prev.map(m => m.id === dup.id ? msg : m); // 用真实消息替换乐观消息
          }
          return [...prev, msg];
        });
        if (msg.fromEmployee) {
          // 提取最后1-2行作为气泡摘要（通常是结论/下一步行动）
          const lines = msg.content.split(String.fromCharCode(10)).filter((l: string) => l.trim());
          const summary = lines.length > 1 ? lines.slice(-2).join(' ').slice(0, 100) : msg.content.slice(0, 80);
          const b = { empId: msg.fromEmployee.id, content: summary };
          bubbleSetRef.current(b.empId, b.content);
        }
        // 问题弹窗已禁用——PM被告知不要向用户提问确认，避免打扰用户
        // if (msg.fromEmployee?.role === 'PM' && msg.content && !msg.content.includes('委派')) { const qs = parseQuestions(msg.content); if (qs?.length) { setQuestionPopup({ fromEmp: msg.fromEmployee, title: `项目经理 ${msg.fromEmployee.name} 向你确认问题`, questions: qs }); setPopupAnswers(new Array(qs.length).fill('')); } }
      }
    });
    socket.on('office:thinking_start', (d: any) => { if (!isCurrentProjectEvent(d)) return; console.log('[DM] WS thinking_start:', d.empName); setThinkingProcess(prev => ({ ...prev, [d.empId]: { empId: d.empId, empName: d.empName, role: d.role, thinking: d.thinking || '让我想想...', status: 'thinking' as const } })); });
    socket.on('office:thinking_update', (d: any) => { if (!isCurrentProjectEvent(d)) return; setThinkingProcess(prev => ({ ...prev, [d.empId]: { empId: d.empId, empName: d.empName, role: d.role, thinking: d.thinking, status: 'thinking' as const } })); });
    socket.on('office:thinking_end', (d: any) => { if (!isCurrentProjectEvent(d)) return; setThinkingProcess(prev => { const cur = prev[d.empId]; if (!cur) return prev; const next = { ...prev, [d.empId]: { ...cur, status: 'done' as const } }; setTimeout(() => setThinkingProcess(p => { const n = { ...p }; delete n[d.empId]; return n; }), 3000); return next; }); });
    socket.on('office:bubble', (d: any) => {
      if (!isCurrentProjectEvent(d)) return;
      console.log('[DM] WS bubble:', d.empId, d.text);
      bubbleSetRef.current(d.empId, d.text);
    });
    socket.on('office:need_role', (d: any) => {
      if (!isCurrentProjectEvent(d)) return;
      console.log('[DM] WS need_role:', d.mention);
      // 解析mention到实际角色
      const roleMap: Record<string,string> = { '产品经理':'PMgr', '项目经理':'PM', '架构师':'Architect', 'ui美工':'UIDesigner', 'ui设计师':'UIDesigner', '研发':'Developer', '研发人员':'Developer', '前端':'Developer', '后端':'Developer', '开发':'Developer', '测试':'Tester', '测试工程师':'Tester' };
      const role = roleMap[d.mention?.toLowerCase()] || d.mention;
      Modal.confirm({
        title: '角色不存在',
        content: d.message || `${d.fromName} 需要 ${d.mention} 角色，是否创建？`,
        okText: '立即创建',
        cancelText: '暂不',
        onOk: async () => {
          try {
            await api.post('/digital-employees/employees', { projectId: d.projectId, role });
            const { data } = await api.get(`/digital-employees/employees?projectId=${d.projectId}`);
            setEmployees(data);
            message.success(`已创建${d.mention}角色`);
          } catch { message.error('创建失败'); }
        },
      });
    });
    socket.on('office:suggest_hire', (d: any) => {
      if (!isCurrentProjectEvent(d)) return;
      console.log('[DM] WS suggest_hire:', d.roles);
      // 招聘确认优先级高于 PM idle/下一步确认，避免两个 Modal 连续抢焦点。
      setNeedUserInput(null);
      setSuggestHire({ roles: d.roles || [], empName: d.empName, projectId: d.projectId });
    });
    // 团队已齐备 → 弹窗询问是否进入下一步
    socket.on('office:need_user_input', (d: any) => {
      if (!isCurrentProjectEvent(d)) return;
      console.log('[DM] WS need_user_input:', d.question);
      // 【2026-07-03 修复】旧逻辑三种情况直接 return 丢弃事件（招聘弹窗挂起 / 用户3秒内刚回复 / 同题去重），
      // 会把 PM 合法的下一个确认"吞掉"，右下角入口也不出现，用户只能干等。
      // 现在改为：压制场景下不抢焦点弹大窗，但必须以最小化入口（右下角闪烁按钮）呈现，事件不丢。
      const hireSuppressed = suggestHireRef.current && suggestHireRef.current.projectId === d.projectId;
      const justReplied = Date.now() - lastReplyAtRef.current < 3000;
      const lastHash = (() => { try { return localStorage.getItem('do-last-reply-hash'); } catch { return null; } })();
      const sameQuestion = !!(lastHash && String(d.question || '').slice(0, 120) === lastHash);
      const minimized = !!(hireSuppressed || justReplied || sameQuestion);
      if (minimized) console.log('[DM] WS need_user_input minimized instead of dropped', { hireSuppressed, justReplied, sameQuestion });
      setUserReply('');
      const projectId = d.projectId || currentProjectRef.current?.id;
      if (projectId) {
        restoreProjectRuntimeState(projectId, { openConfirmation: !minimized }).catch(e => {
          console.warn('[DM] restore pending confirmation after WS need_user_input failed:', e);
          setNeedUserInput({ ...d, _minimized: minimized });
          setUserReply('');
        });
      } else {
        setNeedUserInput({ ...d, _minimized: minimized });
        setUserReply('');
      }
    });
    // 委派事件 → 被委派者立即显示收到任务 + 更新工作链
    socket.on('office:delegate', (d: any) => {
      if (!isCurrentProjectEvent(d)) return;
      console.log('[DM] WS delegate:', d.from?.name, '→', d.to?.name);
      const chainTs = Date.now();
      const nextBubbles = speechBubblesRef.current.filter(x => x.empId !== d.to?.id && x.empId !== d.from?.id);
      speechBubblesRef.current = nextBubbles;
      if (d.from?.id && d.to?.name) bubbleSetRef.current(d.from.id, `我去找 ${d.to.name} 沟通一下`);
      if (d.to?.id) bubbleSetRef.current(d.to.id, `📋 ${d.from?.name || ''}${d.verb || '委派'}了任务，正在思考...`);
      setTaskChain(prev => [...prev, { fromName: d.from?.name, fromRole: d.from?.role, toName: d.to?.name, toRole: d.to?.role, verb: d.verb, task: d.task || '', time: new Date().toLocaleTimeString(), ts: chainTs }]);
    });
    // 员工状态更新 → 刷新画布
    socket.on('office:employee_updated', (d: any) => {
      if (!isCurrentProjectEvent(d)) return;
      setEmployees(prev => prev.map(e => e.id === d.id ? { ...e, ...d } : e));
      setSelectedAgent(prev => prev && prev.id === d.id ? { ...prev, ...d } : prev);
      if (d.status === 'idle') {
        speechBubblesRef.current = speechBubblesRef.current.filter(x => x.empId !== d.id);
        if (bubbleTimers.current[d.id]) { clearTimeout(bubbleTimers.current[d.id]); delete bubbleTimers.current[d.id]; }
        setTaskMap(prev => { const next = { ...prev }; delete next[d.id]; return next; });
        setThinkingProcess(prev => { const next = { ...prev }; delete next[d.id]; return next; });
        delete thinkingRef.current[d.id];
      }
    });
    // 【2026-07-05 问题2】PM招聘完成（recruit_employee / delegate自动招聘 / 手动创建）后端都会广播
    // office:employee_added，此前前端没有监听导致新员工必须刷新页面才出现。
    socket.on('office:employee_added', (d: any) => {
      if (!isCurrentProjectEvent(d)) return;
      console.log('[DM] WS employee_added:', d.name, d.role);
      setEmployees(prev => prev.some(e => e.id === d.id) ? prev : [...prev, d]);
      if (d.name) message.success(`${d.name} 入职成功！`);
    });
    // 员工被解聘后同步移除，避免画布上残留"幽灵员工"
    socket.on('office:employee_deleted', (d: any) => {
      if (!isCurrentProjectEvent(d)) return;
      setEmployees(prev => prev.filter(e => e.id !== d.id));
      setSelectedAgent(prev => prev && prev.id === d.id ? null : prev);
    });
    // 【2026-07-04 问题5】后端对叠在同一坐标的历史数据做工位重排后广播新坐标，前端同步刷新画布
    socket.on('office:employees_reflowed', (d: any) => {
      if (!isCurrentProjectEvent(d)) return;
      const movedMap = new Map<string, [number, number]>((d.moved || []).map((m: any) => [m.id, m.to]));
      if (movedMap.size === 0) return;
      console.log('[DM] WS employees_reflowed:', movedMap.size);
      setEmployees(prev => prev.map(e => {
        const to = movedMap.get(e.id);
        return to ? { ...e, positionX: to[0], positionY: to[1] } : e;
      }));
    });
    socket.on('office:state_event', (d: OfficeStateEvent) => {
      const cp = currentProjectRef.current;
      if (cp && (d as any).projectId === cp.id) {
        setStateEvents(prev => [d, ...prev.filter(x => x.id !== d.id)].slice(0, 30));
      }
    });
    // 【2026-07-04 修复】确认弹窗跨端同步关闭：确认在其他标签页/入口被回答，或被 PM 新确认替代时，
    // 后端广播此事件，本地必须关闭对应弹窗——否则出现"流程在走但弹窗还挂着"的假死观感。
    socket.on('office:confirmation_closed', (d: any) => {
      if (!isCurrentProjectEvent(d)) return;
      console.log('[DM] WS confirmation_closed:', d.confirmationId || '(all of pm)', d.reason);
      if (d.confirmationId) setPendingConfirmations(prev => prev.filter(c => c.id !== d.confirmationId));
      const cur = needUserInputRef.current;
      if (!cur) return;
      const matchById = d.confirmationId && cur.confirmationId === d.confirmationId;
      const matchBySupersede = d.reason === 'superseded' && cur.empId === d.pmEmpId && !cur.confirmationId;
      const matchByPmAnswered = d.reason === 'answered' && cur.empId === d.pmEmpId && !cur.confirmationId;
      if (matchById || matchBySupersede || matchByPmAnswered) {
        setNeedUserInput(null);
        setUserReply('');
      }
    });
    // 【2026-07-03 新增】后端一直在发 thinking_error（LLM未配置/执行失败等），前端此前没有监听，
    // 出错时人物气泡凭空消失、用户不知道发生了什么。现在给出友好提示并清理思考态。
    socket.on('office:thinking_error', (d: any) => {
      console.warn('[DM] WS thinking_error:', d?.empName, d?.error);
      const empName = d?.empName || '数字员工';
      message.warning(`${empName} 执行遇到问题：${String(d?.error || '未知错误').slice(0, 120)}`, 5);
      if (d?.empId) {
        setThinkingProcess(prev => { const n = { ...prev }; delete n[d.empId]; return n; });
        bubbleSetRef.current(d.empId, `⚠️ 出错了：${String(d?.error || '').slice(0, 60)}`);
      }
    });
    return () => { socket.disconnect(); socketRef.current = null; };
  }, [authUser?.id]);

  const parseQuestions = (c: string) => {
    const qs: string[] = [];
    // 多种问题格式检测
    c.split('\n').forEach(l => {
      const t = l.trim();
      if (!t) return;
      // 1. 标准格式: 1. xxx? 或 1、xxx？
      let m = t.match(/^(\d+)[\.\)、]\s*(.+[？?])/);
      if (m) { qs.push(m[2]); return; }
      // 2. Tab分隔格式: 1\t标题\t说明 (任何包含?的列)
      const parts = t.split('\t');
      if (parts.length >= 3 && /^\d+$/.test(parts[0])) {
        const q = parts.find(p => /[？?]/.test(p));
        if (q) { qs.push(`${parts[1] || ''}: ${q}`); return; }
        // 待澄清/待确认/阻塞 等标记
        if (/待确认|待澄清|是否正确|请确认|阻塞/.test(t)) {
          qs.push(`${parts[1] || ''}: ${parts[2] || ''}`);
          return;
        }
      }
      // 3. "请确认/是否/你更倾向" 等确认语句
      if (/^(请你?确认|是否|你更倾向|可以逐条)/.test(t) && t.length > 10) {
        qs.push(t);
      }
    });
    return qs.length > 0 ? qs : null;
  };
  const dismissPopup = () => { setSuspendedPopup({ ...questionPopup, answers: [...popupAnswers] }); setQuestionPopup(null); };

  const submitAnswers = async () => { if (!questionPopup || !currentProject) return; const a = popupAnswers.map((ans, i) => `${i+1}. ${questionPopup.questions[i]}\n   答: ${ans || '(未回答)'}`).join('\n\n'); setInputMsg(`[回复]\n${a}`); setQuestionPopup(null); };
  const trainAgent = async () => { if (!selectedAgent || !trainContent.trim()) return; try { await api.post(`/digital-employees/employees/${selectedAgent.id}/train`, { content: trainContent }); message.success('培训完成'); setTrainOpen(false); } catch (e: any) { message.error('培训失败'); } };

  const isDeliveryReportInput = (input: any) => {
    if (!input) return false;
    const type = String(input.type || '').trim();
    const title = String(input.title || '').trim();
    const question = String(input.question || input.content || '').trim();
    return type === 'delivery_report' || /交付报告/.test(title) || /请确认是否接受本次交付|接受本次交付|提出修改意见/.test(question);
  };

  const answerNeedUserInput = async (answer: string) => {
    const input = needUserInputRef.current || needUserInput;
    if (!input) {
      console.warn('[DO_CONFIRM] ignored: needUserInput is empty', { answer });
      message.warning('当前没有可提交的确认弹窗，请刷新后重试');
      return;
    }
    if (userReplySubmitting) {
      console.warn('[DO_CONFIRM] ignored: submit is already in progress', { answer, input });
      return;
    }
    const empId = input.empId;
    const projectId = input.projectId || currentProjectRef.current?.id;
    let confirmationId = input.confirmationId;
    console.log('[DO_CONFIRM] submit start', { answer, confirmationId, projectId, empId, type: input.type, title: input.title });
    setUserReplySubmitting(true);
    try {
      if (!confirmationId && projectId) {
        console.log('[DO_CONFIRM] confirmationId missing, fetching pending confirmations', { projectId, empId });
        try {
          const { data } = await api.get(`/digital-employees/projects/${projectId}/pending-confirmations`);
          const pending = Array.isArray(data) ? data : [];
          const matched = pending.find((item: any) => item.pmEmpId === empId || item.pm?.id === empId)
            || (isDeliveryReportInput(input) ? pending.find((item: any) => item.type === 'delivery_report') : null)
            || pending[0];
          confirmationId = matched?.id;
          console.log('[DO_CONFIRM] pending confirmations fetched', { count: pending.length, matchedId: confirmationId, matchedTitle: matched?.title });
        } catch (e: any) {
          console.error('[DO_CONFIRM] fetch pending confirmation before answer failed', e?.response?.data || e);
        }
      }
      if (confirmationId) {
        console.log('[DO_CONFIRM] POST answer', { confirmationId, answer });
        const { data } = await api.post(`/digital-employees/confirmations/${confirmationId}/answer`, { answer });
        console.log('[DO_CONFIRM] POST answer success', data);
        setPendingConfirmations(prev => prev.filter(item => item.id !== confirmationId));
      } else {
        if (!projectId || !empId) throw new Error(`缺少确认ID且无法回退发消息：projectId=${projectId || '空'} empId=${empId || '空'}`);
        console.warn('[DO_CONFIRM] fallback POST message because confirmationId is missing', { projectId, empId, answer });
        const { data } = await api.post(`/digital-employees/projects/${projectId}/messages`, { content: answer, toEmpId: empId });
        console.log('[DO_CONFIRM] fallback message success', data);
      }
      setNeedUserInput(null);
      setUserReply('');
      setPopupAnswers([]);
      lastReplyAtRef.current = Date.now();
      try { localStorage.setItem('do-last-reply-hash', String(input.question || '').slice(0,120)); } catch {}
      message.success(isDeliveryReportInput(input) && answer === '确认交付' ? '已确认交付，任务已结束' : '已回复');
    } catch (e: any) {
      console.error('[DO_CONFIRM] submit failed', e?.response?.data || e);
      const msg = e?.response?.data?.message || e?.message || '回复发送失败';
      message.error(`回复发送失败：${msg}`);
    } finally {
      setUserReplySubmitting(false);
    }
  };

  const acceptDeliveryReport = async () => {
    const supplement = userReply.trim();
    if (supplement) {
      // 已填写意见时，主按钮必须直接按补充确认处理；不再二次弹窗，避免嵌套 Modal 让用户点击后无反馈。
      console.log('[DO_CONFIRM] accept button converted to supplement answer', { supplement });
      await answerNeedUserInput(`补充确认：${supplement}`);
      return;
    }
    console.log('[DO_CONFIRM] accept delivery report', { confirmationId: needUserInputRef.current?.confirmationId });
    await answerNeedUserInput('确认交付');
  };

  const requestDeliverySupplement = async () => {
    const supplement = userReply.trim();
    console.log('[DO_CONFIRM] supplement button clicked', { supplement, needUserInput: needUserInputRef.current || needUserInput });
    if (!supplement) {
      message.warning('补充确认必须填写具体修改意见');
      return;
    }
    await answerNeedUserInput(`补充确认：${supplement}`);
  };

  // Auto-scroll conversation to bottom
  useEffect(() => { if (convContainerRef.current) { convContainerRef.current.scrollTop = convContainerRef.current.scrollHeight; } }, [conversations]);

  const toggleConversationPanel = () => {
    setConversationPanelCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('do-left-chat-collapsed', next ? '1' : '0');
      return next;
    });
  };

  const closeConversationDetail = () => {
    setSelectedConversation(null);
    setConversationDetailMode('detail');
    setConversationFlow(null);
    setSelectedFlowNodeId(null);
    setConversationFlowError('');
    setConversationFlowLoading(false);
    flowDragRef.current.active = false;
    setFlowDragActive(false);
  };

  const openConversationDetail = (msg: ConvMsg) => {
    setSelectedConversation(msg);
    setConversationDetailMode('detail');
    setConversationFlow(null);
    setSelectedFlowNodeId(null);
    setConversationFlowError('');
    setConversationFlowLoading(false);
  };

  const openConversationFlow = async (msg: ConvMsg) => {
    setSelectedConversation(msg);
    setConversationDetailMode('flow');
    setConversationFlow(null);
    setSelectedFlowNodeId(null);
    setConversationFlowError('');
    setConversationFlowLoading(true);
    flowDragRef.current.active = false;
    setFlowDragActive(false);
    try {
      const { data } = await api.get(`/digital-employees/conversations/${msg.id}/flow`);
      setConversationFlow(data || null);
    } catch (e: any) {
      console.error('[FLOW] load conversation flow failed', e);
      setConversationFlowError(e?.response?.data?.message || e?.message || '流程数据加载失败');
    } finally {
      setConversationFlowLoading(false);
    }
  };

  const handleFlowDragStart = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = flowScrollRef.current;
    if (!el) return;
    flowDragRef.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: el.scrollLeft,
      moved: false,
    };
    setFlowDragActive(true);
    el.setPointerCapture?.(event.pointerId);
  };

  const handleFlowDragMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = flowScrollRef.current;
    const drag = flowDragRef.current;
    if (!el || !drag.active) return;
    const delta = event.clientX - drag.startX;
    if (Math.abs(delta) > 4) drag.moved = true;
    el.scrollLeft = drag.scrollLeft - delta;
    event.preventDefault();
  };

  const handleFlowDragEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = flowScrollRef.current;
    if (el?.hasPointerCapture?.(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
    flowDragRef.current.active = false;
    setFlowDragActive(false);
    setTimeout(() => {
      flowDragRef.current.moved = false;
    }, 0);
  };

  // 【2026-07-05 问题3】点卡片除了高亮，还打开详情弹窗（含被合并的沟通记录全文）
  const handleFlowCardClick = (group: FlowDisplayGroup) => {
    if (flowDragRef.current.moved) return;
    setSelectedFlowNodeId(group.primary.id);
    setFlowDetailGroup(group);
  };

  // Auto-clear speech bubbles after 60s
  const bubbleTimers = useRef<Record<string, NodeJS.Timeout>>({});
  const setBubble = (empId: string, content: string) => {
    speechBubblesRef.current = [...speechBubblesRef.current.filter(x => x.empId !== empId), { empId, content }];
    if (bubbleTimers.current[empId]) clearTimeout(bubbleTimers.current[empId]);
    bubbleTimers.current[empId] = setTimeout(() => {
      speechBubblesRef.current = speechBubblesRef.current.filter(x => x.empId !== empId);
      delete bubbleTimers.current[empId];
    }, 60000);
  };
  // Update bubble setters to use setBubble
  const bubbleSet = (empId: string, content: string) => { setBubble(empId, content); };
  // Expose for use in socket handlers via ref
  const bubbleSetRef = useRef(bubbleSet);
  useEffect(() => { bubbleSetRef.current = bubbleSet; });

  // Project picker
  if (!currentProject) {
    return (
      <div className="digital-office-page digital-office-picker do-picker">
        <div className="do-picker-bg" aria-hidden="true">
          <span className="do-picker-orb do-picker-orb-1" />
          <span className="do-picker-orb do-picker-orb-2" />
          <span className="do-picker-orb do-picker-orb-3" />
        </div>
        <div className="digital-office-header do-picker-header">
          <Space size={14}>
            <span className="do-picker-logo"><RobotOutlined /></span>
            <div>
              <Title level={4} style={{ margin: 0 }}>数字办公室 · SmartOffice</Title>
              <Text className="do-picker-brand-sub">2.5D 数字员工协作空间</Text>
            </div>
          </Space>
        </div>
        <div className="digital-office-picker-body do-picker-body">
          <div className="do-picker-hero">
            <span className="do-picker-hero-badge">🏢 WORKSPACE</span>
            <Title level={2} className="do-picker-hero-title">选择一个工作台项目开始</Title>
            <Text className="do-picker-hero-sub">数字员工归属工作台项目，请在下方选择已有项目</Text>
          </div>
          {wsProjects.length === 0 ? (
            <div className="do-picker-empty">
              <span className="do-picker-empty-icon">🗂️</span>
              <Text className="do-picker-empty-text">暂无工作台项目，请先在工作台中创建项目</Text>
            </div>
          ) : (
            <div className="do-picker-grid">
              {wsProjects.map((p, i) => (
                <div key={p.id} className="do-picker-card" style={{ animationDelay: `${Math.min(i, 8) * 70}ms` }} onClick={() => selectProject(p)}>
                  <div className="do-picker-card-top">
                    <span className={`do-picker-card-avatar do-hue-${i % 4}`}>{(p.name || '?').slice(0, 1)}</span>
                    <span className="do-picker-card-arrow"><RightOutlined /></span>
                  </div>
                  <Text strong className="do-picker-card-name">{p.name}</Text>
                  <div className="do-picker-card-meta">
                    <span className="do-picker-card-chip"><TeamOutlined />{p.digitalEmployeeCount || 0} 名数字员工</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const defaultTargetAgent = findProjectManager(employees);
  const targetLabel = defaultTargetAgent
    ? `${ROLE_CONFIG[defaultTargetAgent.role]?.label || defaultTargetAgent.role}-${defaultTargetAgent.name}`
    : '项目经理';
  const assetPercent = Math.min(100, Math.round((assetLoading.done / Math.max(1, assetLoading.total)) * 100));

  return (
    <div className="digital-office-page digital-office-work sim-office-page" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: '"Microsoft YaHei", sans-serif' }}>
      <div className={`digital-office-header sim-office-topbar sim-unified-toolbar ${topbarCollapsed ? 'is-collapsed' : ''}`} style={{ zIndex: 95 }}>
        {topbarCollapsed ? (
          <Button className="sim-toolbar-reveal" onClick={() => { setTopbarCollapsed(false); localStorage.setItem('do-toolbar-collapsed','0'); }}>☰ 工作台工具栏</Button>
        ) : (
          <div className="sim-toolbar-glass">
            <div className="sim-toolbar-project">
              <RobotOutlined className="sim-office-logo" />
              <div className="sim-project-title"><Title level={5} style={{ margin: 0 }}>{currentProject.name}</Title><Text className="sim-office-subtitle">2.5D 数字办公室</Text></div>
            </div>
            <div className="sim-toolbar-actions">
              <div className="sim-toolbar-control sim-toolbar-control-scene">
                <Select className="sim-scene-select" size="middle" value={sceneIndex} onChange={(next) => { setSceneIndex(next); localStorage.setItem('do-scene-idx', String(next)); }} options={OFFICE_SCENES.map((s, i) => ({ value: i, label: `🏢 ${s.name}` }))} />
              </div>
              <div className="sim-toolbar-cluster sim-toolbar-cluster-team">
                <Button className="sim-tool-btn" size="middle" icon={<BookOutlined />} onClick={() => { setRulesLoading(true); setRulesModalOpen(true); api.get(`/digital-employees/projects/${currentProject.id}/rules`).then(({data})=>{setProjectRules(data||[]);}).catch(()=>{}).finally(()=>setRulesLoading(false)); }}>规则</Button>
                <Button className="sim-tool-btn" size="middle" icon={<UserAddOutlined />} onClick={openHireModal}>招聘</Button>
              </div>
              <div className="sim-toolbar-cluster sim-toolbar-cluster-data">
                <Button className="sim-tool-btn" size="middle" icon={<FolderOutlined />} onClick={showFileTree ? () => setShowFileTree(false) : loadFileTree} loading={treeLoading}>文件</Button>
                <Button className="sim-tool-btn" size="middle" icon={<BookOutlined />} onClick={async () => { try { const { data } = await api.get(`/digital-employees/projects/${currentProject.id}/conversations?limit=200`); setHistoryData(data||[]); setHistoryOpen(true); } catch (e: any) { message.error('加载历史失败'); } }}>历史</Button>
                <Button className="sim-tool-btn" size="middle" onClick={() => setStateEventsOpen(true)}>动态{pendingConfirmations.length > 0 ? `·${pendingConfirmations.length}` : ''}</Button>
              </div>
              <div className="sim-toolbar-cluster sim-toolbar-cluster-system">
                <Button className="sim-tool-btn" size="middle" onClick={async () => { if (allResting) { try { await api.post(`/digital-employees/projects/${currentProject.id}/resume-all`); setAllResting(false); message.success('已恢复工作'); } catch {} } else { try { await api.post(`/digital-employees/projects/${currentProject.id}/pause-all`); setAllResting(true); message.success('已暂停'); } catch {} } }}>{allResting ? '▶ 恢复' : '⏸ 暂停'}</Button>
                <Button className="sim-tool-btn" size="middle" danger onClick={async () => { try { const pid = currentProject.id; await api.post(`/digital-employees/projects/${pid}/reset-state`, { reason: '用户在页面上执行状态重置' }); setAllResting(false); setTaskMap({}); setThinkingProcess({}); thinkingRef.current = {}; speechBubblesRef.current = []; setNeedUserInput(null); const [eRes, cRes] = await Promise.all([api.get(`/digital-employees/employees?projectId=${pid}`), api.get(`/digital-employees/projects/${pid}/conversations?limit=50`)]); setEmployees(eRes.data || []); setConversations(cRes.data || []); await restoreProjectRuntimeState(pid); message.success('已重置所有状态'); } catch (e: any) { message.error(e.response?.data?.message || '重置失败'); } }}>重置状态</Button>
                <Button className="sim-tool-btn" size="middle" icon={<ReloadOutlined />} onClick={() => { setCurrentProject(null); loadWorkspaceProjects(); }}>切换</Button>
                <Button className="sim-tool-min" size="middle" type="text" onClick={() => { setTopbarCollapsed(true); localStorage.setItem('do-toolbar-collapsed','1'); }}>收起</Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="sim-office-main" style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        <div className="sim-office-stage" style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} onMouseDown={assetLoading.ready ? handleMouseDown : undefined} onMouseMove={assetLoading.ready ? handleMouseMove : undefined} onMouseUp={assetLoading.ready ? handleMouseUp : undefined} onMouseLeave={() => { setHoveredAgent(null); if (draggingId) handleMouseUp(); }} style={{ cursor: !assetLoading.ready ? 'wait' : draggingId ? 'grabbing' : hoveredAgent ? 'grab' : 'default', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
          {Object.values(thinkingProcess).filter(t => t.status === 'thinking').map(t => {
            const emp = employees.find(e => e.id === t.empId);
            const pose = emp ? (walkingPoseRef.current.get(emp.id) || { x: emp.positionX, y: emp.positionY }) : null;
            if (!pose) return null;
            // Convert canvas coords to percentage-based positioning
            const leftPct = (pose.x / CANVAS_W) * 100;
            const topPct = ((pose.y - 200) / CANVAS_H) * 100;
            return (
              <div key={t.empId} className="sim-thinking-float-icon"
                style={{ position: 'absolute', left: `${leftPct}%`, top: `${topPct}%`, transform: 'translateX(-50%)', zIndex: 30 }}
                onClick={() => { setThinkingDetailEmpId(t.empId); setThinkingDetailOpen(true); }}
              >
                <span className="sim-thinking-pulse-dot" />
                <span className="sim-thinking-icon-text">💭</span>
              </div>
            );
          })}
          {!assetLoading.ready && (
            <div className="sim-game-loader">
              <div className="sim-game-loader-card">
                <div className="sim-loader-orbit"><span /><span /><span /></div>
                <div className="sim-loader-title">正在进入数字办公室</div>
                <div className="sim-loader-subtitle">预加载全部场景图、人物模型和动作帧，加载完成后再展示</div>
                <div className="sim-loader-bar"><i style={{ width: `${assetPercent}%` }} /></div>
                <div className="sim-loader-meta"><span>{assetLoading.label}</span><b>{assetPercent}%</b></div>
              </div>
            </div>
          )}
          <div ref={progressBoardRef} className="sim-progress-board" onMouseDown={handleProgressMouseDown} style={{ position: 'absolute', top: progressBoardPos.y, left: progressBoardPos.x, width: 360, zIndex: 70, cursor: 'grab' }}>
            <div className="sim-progress-head">
              <div><Text strong className="sim-panel-title">工作调度看板</Text><Text className="sim-panel-subtitle">实时展示团队正在做什么</Text></div>
              <Button className="sim-board-refresh" size="small" type="text" icon={progressLoading ? <LoadingOutlined /> : <ReloadOutlined />} onClick={evaluateProgress} loading={progressLoading}>{progressLoading ? '评估中' : '刷新'}</Button>
            </div>
            <div className="sim-progress-list">
              {(() => {
                // 以 employees (WS实时) 为唯一权威源，progressMembers 仅用于补充task详情
                const activeEmps = employees.filter(e => e.status !== 'idle');
                if (activeEmps.length === 0) {
                  return <div className="sim-progress-empty">当前没有活跃任务；可选择项目经理输入新需求。</div>;
                }
                const pmByName: Record<string, any> = {};
                progressMembers.forEach(m => { pmByName[m.name] = m; });
                return activeEmps.map(emp => {
                  const pm = pmByName[emp.name];
                  const st = STATUS_CONFIG[emp.status];
                  const stIcon = emp.status === 'thinking' ? '💭' : emp.status === 'waiting_user' ? '⏳' : emp.status === 'working' ? '⚙️' : '•';
                  const displayTitle = pm?.displayTitle || st?.label || '处理中';
                  const displaySummary = pm?.displaySummary || pm?.doing;
                  const displayWaiting = pm?.waiting;
                  return <div key={emp.id} className={`sim-progress-item sim-progress-${emp.status}`}>
                    <div className="sim-progress-row">
                      <span className="sim-progress-icon">{stIcon}</span>
                      <span className="sim-progress-name" style={{ color: ROLE_CONFIG[emp.role]?.color || '#2563eb' }}>{ROLE_CONFIG[emp.role]?.label}-{emp.name}</span>
                      <span className="sim-progress-status" style={{ color: st?.color || '#64748b' }}>{displayTitle}</span>
                    </div>
                    {displaySummary && <div className="sim-progress-task">📋 {displaySummary}</div>}
                    {displayWaiting && <div className="sim-progress-waiting">⏳ {displayWaiting}</div>}
                  </div>;
                });
              })()}
            </div>
          </div>
          {employees.length === 0 && <Empty description="还没有员工" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />}
          {suspendedPopup && <div onClick={() => { setQuestionPopup(suspendedPopup); setPopupAnswers(suspendedPopup.answers || []); setSuspendedPopup(null); }} style={{ position: 'absolute', bottom: 60, right: 16, background: 'linear-gradient(135deg, #f5a623, #ff7875)', color: '#fff', borderRadius: 24, padding: '10px 18px', cursor: 'pointer', zIndex: 60, boxShadow: '0 4px 16px rgba(245,166,35,0.4)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>📋 {suspendedPopup.fromEmp?.name} 的问题待回复 ↩</div>}
          {questionPopup && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={dismissPopup}><div className="digital-office-question-card" style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 480, width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.2)', border: '2px solid #f5a623' }} onClick={e => e.stopPropagation()}><div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>{questionPopup.title}</div>{questionPopup.questions.map((q: string, i: number) => <div key={i} style={{ marginBottom: 12 }}><div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{i+1}. {q}</div><TextArea value={popupAnswers[i] || ''} onChange={e => { const n = [...popupAnswers]; n[i] = e.target.value; setPopupAnswers(n); }} placeholder="输入回答..." autoSize={{ minRows: 2, maxRows: 4 }} /></div>)}<div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}><Button onClick={dismissPopup}>稍后回复</Button><Button type="primary" onClick={submitAnswers} style={{ background: '#f5a623', borderColor: '#f5a623' }}>提交</Button></div></div></div>}
          {/* 文件结构面板 */}
          {showFileTree && <div className="digital-office-file-tree"
            style={{ position: 'absolute', top: 10, left: 10, width: 280, maxHeight: '60%', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', borderRadius: 8, padding: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: treeDragOver ? '2px dashed #2563eb' : '2px solid #d9c9a5', zIndex: 55, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!treeDragOver) setTreeDragOver(true); }}
            onDragLeave={e => { e.preventDefault(); if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setTreeDragOver(false); }}
            onDrop={e => { e.preventDefault(); e.stopPropagation(); setTreeDragOver(false); const dropped = Array.from(e.dataTransfer?.files || []); if (dropped.length) uploadWorkspaceFiles(dropped); }}>
            <input ref={uploadInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => { const picked = Array.from(e.target.files || []); e.target.value = ''; uploadWorkspaceFiles(picked); }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexShrink: 0 }}>
              <Text strong style={{ fontSize: 13, color: '#5c4a2e' }}>📁 产出文件</Text>
              <Space size={4}>
                <Tooltip title="上传文件到当前工作区（也可直接拖拽文件到本面板）"><Button size="small" type="text" icon={<UploadOutlined />} onClick={() => uploadInputRef.current?.click()} loading={uploading} style={{ color: '#8b7355', fontSize: 11 }} /></Tooltip>
                <Button size="small" type="text" icon={treeLoading ? <LoadingOutlined /> : <ReloadOutlined />} onClick={loadFileTree} loading={treeLoading} style={{ color: '#8b7355', fontSize: 11 }} />
                <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setShowFileTree(false)} style={{ color: '#999' }} />
              </Space>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6, flexShrink: 0, fontSize: 11, color: '#8b7355', background: 'rgba(217,201,165,0.25)', borderRadius: 4, padding: '2px 6px' }}>
              <UploadOutlined style={{ fontSize: 11 }} />
              <Tooltip title={uploadTargetDir ? `上传目标目录：${uploadTargetDir}` : '当前上传到工作区根目录，点击左侧文件夹可切换上传位置'}>
                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default' }}>上传至：{uploadTargetDir ? uploadTargetDir.split('/').filter(Boolean).pop() : '根目录'}</span>
              </Tooltip>
              {uploadTargetDir && <Tooltip title="改回根目录"><Button size="small" type="text" icon={<CloseOutlined style={{ fontSize: 9 }} />} onClick={() => setUploadTargetDir('')} style={{ color: '#8b7355', width: 16, height: 16, minWidth: 16 }} /></Tooltip>}
            </div>
            <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
              {fileTree.length > 0 ? <Tree showIcon treeData={toTreeData(fileTree)} defaultExpandAll selectedKeys={uploadTargetDir ? [uploadTargetDir] : []} style={{ fontSize: 12 }} onSelect={async (keys, info: any) => {
                if (info.node.isLeaf) {
                  try { const { data } = await api.get(`/digital-employees/projects/${currentProject?.id}/file-content`, { params: { path: info.node.path } }); if (data.content) setFileViewer(data); else message.warning(data.error); } catch { message.error('读取失败'); }
                } else {
                  setUploadTargetDir(info.selected ? String(info.node.key) : '');
                }
              }} /> : <Empty description={<span style={{ fontSize: 12 }}>暂无产出文件<br />点击上方 <UploadOutlined /> 或拖拽文件到此处上传</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
              {treeDragOver && <div style={{ position: 'absolute', inset: 0, background: 'rgba(37,99,235,0.08)', border: '1px dashed #2563eb', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', fontSize: 12, color: '#2563eb', fontWeight: 600 }}>松开即上传到「{uploadTargetDir ? uploadTargetDir.split('/').filter(Boolean).pop() : '根目录'}」</div>}
            </div>
          </div>}
        </div>
      </div>
      {/* Left conversation timeline */}
      <aside className={`sim-left-chat ${conversationPanelCollapsed ? 'is-collapsed' : ''}`}>
        <Button
          className="sim-left-chat-toggle"
          type="text"
          icon={conversationPanelCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={toggleConversationPanel}
          aria-label={conversationPanelCollapsed ? '展开沟通记录' : '收起沟通记录'}
        />
        <div className="sim-left-chat-inner">
          <div className="sim-left-chat-head">
            <div>
              <Text strong className="sim-left-chat-title">沟通记录</Text>
              <Text className="sim-left-chat-subtitle">需求、任务与团队对话</Text>
            </div>
            <Tag className="sim-left-chat-count">{conversations.length}</Tag>
          </div>
          <div ref={convContainerRef} className="sim-conversation-list">
            {conversations.length === 0 && <div className="sim-chat-empty">暂无沟通记录</div>}
            {conversations.map(msg => {
              const fc = msg.fromEmployee ? ROLE_CONFIG[msg.fromEmployee.role] : null;
              const tc = msg.toEmployee ? ROLE_CONFIG[msg.toEmployee.role] : null;
              const actor = msg.fromEmployee || msg.toEmployee;
              const actorCfg = actor ? ROLE_CONFIG[actor.role] || ROLE_CONFIG.PM : null;
              const isUserRequest = !msg.fromEmployee;
              const title = msg.fromEmployee ? `${fc?.label || msg.fromEmployee.role}-${msg.fromEmployee.name}` : '我';
              const route = msg.fromEmployee && msg.toEmployee
                ? `${fc?.label || msg.fromEmployee.role} → ${tc?.label || msg.toEmployee.role}`
                : msg.toEmployee
                  ? `发送给 ${tc?.label || msg.toEmployee.role}-${msg.toEmployee.name}`
                  : '系统记录';
              return <div key={msg.id} className={`sim-message-card ${isUserRequest ? 'is-user-request' : ''}`} style={{ ['--msg-color' as any]: actorCfg?.color || '#0ea5e9' }}>
                <div className="sim-message-topline">
                  <span className="sim-message-avatar">{title.slice(0, 1)}</span>
                  <div className="sim-message-meta-main">
                    <Text strong className="sim-message-name">{conversationSummary(msg)}</Text>
                    <span className="sim-message-route">{route}</span>
                  </div>
                  <div className="sim-message-actions">
                    <Button className="sim-message-detail-btn" type="text" size="small" icon={<EyeOutlined />} onClick={() => openConversationDetail(msg)} aria-label="查看本条沟通详情">详情</Button>
                    <Button className="sim-message-flow-btn" type="primary" size="small" icon={<ApartmentOutlined />} onClick={() => openConversationFlow(msg)} aria-label="查看委派流程图">流程图</Button>
                  </div>
                </div>
                <div className="sim-message-brief">{conversationBrief(msg)}</div>
                <div className="sim-message-footer">
                  <span>{isUserRequest ? '需求概要' : '交互概要'}</span>
                  <span>{new Date(msg.createdAt).toLocaleString()}</span>
                </div>
              </div>;
            })}
            <div ref={chatEndRef} />
          </div>
        </div>
      </aside>

      {/* Bottom command deck */}
      <div className="digital-office-bottom sim-command-deck">
        <div className="digital-office-chat-shell sim-command-shell">
          <div className="sim-input-panel sim-input-panel-pm-only">
            <div className="sim-input-target">默认发送给 {targetLabel}，直接输入需求即可</div>
            <TextArea value={inputMsg} onChange={e => setInputMsg(e.target.value)} onPressEnter={sendMessage} placeholder="把需求告诉项目经理，例如：帮我做一个……" className="sim-command-input" autoSize={false} />
            <div className="sim-input-actions">
              <VoiceInputButton value={inputMsg} onChange={setInputMsg} disabled={!defaultTargetAgent} />
              <Button className="sim-send-btn" type="primary" icon={<SendOutlined />} onClick={sendMessage} disabled={!defaultTargetAgent}>发送给项目经理</Button>
            </div>
          </div>
        </div>
      </div>
      {/* Settings drawer */}
      <Drawer className="sim-agent-drawer" rootClassName="sim-agent-drawer-root" title={null} open={drawerOpen && !!selectedAgent} onClose={() => { setDrawerOpen(false); setSelectedAgent(null); }} width={460} styles={{ body: { padding: 0, background: 'linear-gradient(180deg, #111827 0%, #0f172a 100%)', color: '#e2e8f0' } }}>
        {selectedAgent && <div className="sim-agent-drawer-panel">
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: `linear-gradient(135deg, ${ROLE_CONFIG[selectedAgent.role]?.hexColor || '#4f8cff'} 0%, ${ROLE_CONFIG[selectedAgent.role]?.hexColor || '#4f8cff'}88 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 24, fontWeight: 800,
              boxShadow: `0 4px 14px ${ROLE_CONFIG[selectedAgent.role]?.hexColor || '#4f8cff'}44`
            }}>{selectedAgent.name?.charAt(0)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#f8fafc', marginBottom: 4 }}>{selectedAgent.name}</div>
              <Space size={6}>
                <Tag color={ROLE_CONFIG[selectedAgent.role]?.color} style={{ margin: 0, borderRadius: 6, fontWeight: 600 }}>{ROLE_CONFIG[selectedAgent.role]?.label}</Tag>
                <Tag color={statusTone(selectedAgent.status).color} style={{ margin: 0, borderRadius: 6, fontWeight: 600 }}>{statusTone(selectedAgent.status).label}</Tag>
              </Space>
            </div>
          </div>

          {/* Quick actions */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <Button icon={<EditOutlined />} size="small" style={{ flex: 1, borderRadius: 8, fontWeight: 600 }}
              onClick={() => { setEditName(selectedAgent.name); setDetailOpen(true); }}>改名</Button>
            <Button icon={<BookOutlined />} size="small" style={{ flex: 1, borderRadius: 8, fontWeight: 600 }}
              onClick={() => { setTrainOpen(true); setTrainContent(''); }}>培训</Button>
            <Popconfirm title="确定删除？" onConfirm={async () => { try { await api.delete(`/digital-employees/employees/${selectedAgent.id}`); setDrawerOpen(false); setSelectedAgent(null); } catch { message.error('删除失败'); } }}>
              <Button danger size="small" style={{ borderRadius: 8, fontWeight: 600 }}>删除</Button>
            </Popconfirm>
          </div>

          {/* Card: Thinking toggle */}
          <div style={{
            background: selectedAgent.enableThinking !== false ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.04)',
            borderRadius: 12, padding: '14px 16px', marginBottom: 12,
            border: selectedAgent.enableThinking !== false ? '1px solid rgba(139,92,246,0.25)' : '1px solid rgba(255,255,255,0.08)',
            transition: 'all .2s'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#f8fafc', marginBottom: 2 }}>
                  🧠 深度思考
                </div>
                <div style={{ fontSize: 11, color: selectedAgent.enableThinking !== false ? '#c4b5fd' : '#94a3b8' }}>
                  {selectedAgent.enableThinking !== false ? 'AI先推理再回答，思考过程可见' : '关闭后AI直接回复，速度更快'}
                </div>
              </div>
              <Switch checked={selectedAgent.enableThinking !== false} onChange={async (v) => {
                try {
                  await api.put(`/digital-employees/employees/${selectedAgent.id}`, { enableThinking: v });
                  setEmployees(prev => prev.map(e => e.id === selectedAgent.id ? { ...e, enableThinking: v } : e));
                  setSelectedAgent(prev => prev ? { ...prev, enableThinking: v } : prev);
                } catch { message.error('思考开关保存失败'); }
              }} />
            </div>
          </div>

          {/* Card: LLM Model */}
          <div style={{
            background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '14px 16px', marginBottom: 12,
            border: '1px solid rgba(255,255,255,0.08)'
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#f8fafc', marginBottom: 8 }}>
              🤖 大模型配置
            </div>
            <Select
              value={selectedAgent.llmConfigId || undefined}
              onChange={async (v) => {
                try {
                  await api.put(`/digital-employees/employees/${selectedAgent.id}`, { llmConfigId: v });
                  setEmployees(prev => prev.map(e => e.id === selectedAgent.id ? { ...e, llmConfigId: v } : e));
                  // 抽屉读的是 selectedAgent 快照，必须同步更新，否则下拉框显示旧值、用户以为没选上
                  setSelectedAgent(prev => prev ? { ...prev, llmConfigId: v } : prev);
                  message.success('模型已保存');
                } catch { message.error('模型保存失败，请重试'); }
              }}
              style={{ width: '100%' }}
              placeholder="点击选择模型"
              variant="borderless"
              options={availableModels.map(m => ({
                label: typeof m.provider === 'object' ? `${m.provider?.name || '?'} / ${m.model || m.code}` : `${m.provider || '?'} / ${m.model || m.code}`,
                value: m.id
              }))}
            />
            {!selectedAgent.llmConfigId && (
              <div style={{ fontSize: 11, color: '#ef4444', marginTop: 6 }}>⚠️ 未配置模型，该员工收到任务后将进入异常状态</div>
            )}
          </div>

          {/* Card: Hermes Skills */}
          <div style={{
            background: 'linear-gradient(135deg, #ecfeff 0%, #f0fdfa 100%)', borderRadius: 12, padding: '14px 16px', marginBottom: 12,
            border: '1px solid #99f6e4'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>🧩 真实 Skill 加载</div>
              {skillsLoading && <LoadingOutlined style={{ color: '#0891b2' }} />}
            </div>
            <div style={{ fontSize: 11, color: '#0f766e', lineHeight: 1.5, marginBottom: 10 }}>
              这里绑定的是本地 Hermes SKILL.md。绑定后，数字人执行任务前会读取真实方法论并注入上下文。
            </div>
            <Space.Compact style={{ width: '100%', marginBottom: 10 }}>
              <Select
                showSearch
                allowClear
                value={selectedSkillId}
                onChange={setSelectedSkillId}
                style={{ flex: 1 }}
                placeholder="选择本地 Skill"
                optionFilterProp="label"
                popupClassName="sim-agent-skill-dropdown"
                options={localSkills.map(s => ({ label: `${skillDisplayLabel(s)}${s.category ? ` · ${SKILL_CATEGORY_CN[s.category] || s.category}` : ''}`, value: s.skillId }))}
              />
              <Button type="primary" onClick={attachSkillToAgent} disabled={!selectedSkillId}>加载</Button>
            </Space.Compact>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(employeeSkillDetail?.bindings || selectedAgent.skillBindings || []).filter((s: EmployeeSkillBinding) => s.enabled !== false).length === 0 && (
                <Text style={{ fontSize: 12, color: '#64748b' }}>暂无手动绑定，仍会按角色自动匹配本地存在的推荐 Skill。</Text>
              )}
              {(employeeSkillDetail?.bindings || selectedAgent.skillBindings || []).filter((s: EmployeeSkillBinding) => s.enabled !== false).map((s: EmployeeSkillBinding) => (
                <Tag key={s.skillId} color="cyan" closable onClose={(e) => { e.preventDefault(); detachSkillFromAgent(s.skillId); }} style={{ borderRadius: 999, fontWeight: 700 }}>
                  {skillDisplayLabel(s)}
                </Tag>
              ))}
            </div>
            {employeeSkillDetail?.recommended?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #99f6e4' }}>
                <div style={{ fontSize: 11, color: '#0f766e', fontWeight: 800, marginBottom: 6 }}>角色推荐</div>
                <Space wrap size={[4, 4]}>
                  {employeeSkillDetail.recommended.map((s: any) => (
                    <Tag key={s.skillId} color={s.localExists ? 'green' : 'default'} style={{ margin: 0, borderRadius: 6, fontSize: 11 }}>
                      {skillDisplayLabel(s)}{s.localExists ? ' · 本地可加载' : ' · 仅标签'}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}
          </div>

          {/* Card: Token usage + Position */}
          <div style={{
            background: '#fafafa', borderRadius: 12, padding: '14px 16px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>
              📊 用量 & 状态
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <Text style={{ fontSize: 12, color: '#64748b' }}>Token 用量</Text>
              <Text style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{selectedAgent.tokenUsed || 0} / {selectedAgent.tokenBudget || 500000}</Text>
            </div>
            <div style={{ background: '#e5e7eb', borderRadius: 4, height: 6, marginBottom: 10, overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(100, ((selectedAgent.tokenUsed || 0) / (selectedAgent.tokenBudget || 500000)) * 100)}%`,
                height: '100%', borderRadius: 4,
                background: (selectedAgent.tokenUsed || 0) > (selectedAgent.tokenBudget || 500000) * 0.8 ? '#ef4444' :
                  (selectedAgent.tokenUsed || 0) > (selectedAgent.tokenBudget || 500000) * 0.5 ? '#f59e0b' : '#22c55e',
                transition: 'width .4s'
              }} />
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              位置: ({selectedAgent.positionX}, {selectedAgent.positionY})
            </div>
          </div>
        </div>}
      </Drawer>
      <Modal title="修改姓名" open={detailOpen} onOk={async () => { if (!selectedAgent || !editName.trim()) return; try { await api.put(`/digital-employees/employees/${selectedAgent.id}`, { name: editName }); setDetailOpen(false); message.success('改名成功'); } catch {} }} onCancel={() => setDetailOpen(false)}><Input value={editName} onChange={e => setEditName(e.target.value)} /></Modal>
      <Modal
        className="sim-thinking-modal"
        title={null}
        open={thinkingDetailOpen}
        onCancel={() => { setThinkingDetailOpen(false); setThinkingDetailEmpId(null); }}
        footer={<Button className="sim-modal-primary" type="primary" onClick={() => { setThinkingDetailOpen(false); setThinkingDetailEmpId(null); }}>关闭</Button>}
        width={820}
      >
        {(() => {
          const tp = thinkingDetailEmpId ? thinkingProcess[thinkingDetailEmpId] : null;
          const roleText = tp?.role ? (ROLE_CONFIG[tp.role]?.label || tp.role) : '思考中';
          return (
            <div className="sim-thinking-panel">
              <div className="sim-thinking-hero">
                <span className="sim-thinking-hero-icon">💭</span>
                <div>
                  <div className="sim-thinking-kicker">AI 数字员工思考过程</div>
                  <h3>{tp?.empName || '数字员工'} · {roleText}</h3>
                  <p>只展示当前这次推理链路，方便核对它为什么这样安排任务。</p>
                </div>
                <Tag className={`sim-thinking-status ${tp?.status === 'done' ? 'is-done' : 'is-running'}`}>{tp?.status === 'done' ? '思考完成' : '思考中'}</Tag>
              </div>
              <div className="sim-thinking-content">
                {tp?.status === 'thinking' && <div className="sim-thinking-live"><LoadingOutlined /> 正在持续接收思考内容</div>}
                <pre>{tp?.thinking || '暂无思考内容'}</pre>
              </div>
            </div>
          );
        })()}
      </Modal>
      <Modal title={`培训 ${selectedAgent?.name}`} open={trainOpen} onOk={trainAgent} onCancel={() => setTrainOpen(false)}>
        <div className="sim-modal-voice-input">
          <TextArea value={trainContent} onChange={e => setTrainContent(e.target.value)} placeholder="输入培训内容..." autoSize={{ minRows: 4, maxRows: 10 }} />
          <VoiceInputButton value={trainContent} onChange={setTrainContent} size="small" />
        </div>
      </Modal>
      {/* 项目规则管理 */}
      <Modal title="📋 项目规则" open={rulesModalOpen} onCancel={() => { setRulesModalOpen(false); setEditingRule(null); }} width={720} footer={null}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary">这些规则会注入到所有数字员工的系统提示中，确保 AI 不拍脑子决定。</Text>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setEditingRule({ isNew: true, title: '', content: '', category: 'general', priority: 100 })}>新增规则</Button>
        </div>
        {rulesLoading ? <div style={{ textAlign: 'center', padding: 40 }}><LoadingOutlined style={{ fontSize: 24 }} /></div> : projectRules.length === 0 ? <Empty description="暂无规则" /> : (
          <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
            {projectRules.map((r: any) => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', marginBottom: 6, borderRadius: 8, background: r.enabled ? '#f0f9ff' : '#f5f5f5', border: '1px solid ' + (r.enabled ? '#bae6fd' : '#e5e7eb') }}>
                <Switch size="small" checked={r.enabled} onChange={async (v) => { try { await api.put(`/digital-employees/rules/${r.id}`, { enabled: v }); setProjectRules(prev => prev.map(x => x.id === r.id ? { ...x, enabled: v } : x)); } catch { message.error('操作失败'); } }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2, color: r.enabled ? '#0369a1' : '#999' }}>{r.title}</div>
                  <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>{r.content}</div>
                </div>
                <Space size={4}>
                  <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEditingRule({ ...r, isNew: false })} />
                  <Popconfirm title="删除此规则？" onConfirm={async () => { try { await api.delete(`/digital-employees/rules/${r.id}`); setProjectRules(prev => prev.filter(x => x.id !== r.id)); } catch { message.error('删除失败'); } }}>
                    <Button size="small" type="text" danger icon={<CloseOutlined />} />
                  </Popconfirm>
                </Space>
              </div>
            ))}
          </div>
        )}
      </Modal>
      {/* 规则编辑弹窗 */}
      <Modal title={editingRule?.isNew ? '新增规则' : '编辑规则'} open={!!editingRule} onCancel={() => setEditingRule(null)} onOk={async () => {
        if (!editingRule) return;
        const { title, content, category, priority } = editingRule;
        if (!title?.trim() || !content?.trim()) { message.warning('标题和内容不能为空'); return; }
        try {
          if (editingRule.isNew) {
            await api.post(`/digital-employees/projects/${currentProject?.id}/rules`, { title: title.trim(), content: content.trim(), category, priority });
          } else {
            await api.put(`/digital-employees/rules/${editingRule.id}`, { title: title.trim(), content: content.trim(), category, priority });
          }
          const { data } = await api.get(`/digital-employees/projects/${currentProject?.id}/rules`);
          setProjectRules(data || []);
          setEditingRule(null);
          message.success(editingRule.isNew ? '规则已添加' : '规则已更新');
        } catch { message.error('保存失败'); }
      }} okText="保存">
        <div style={{ marginBottom: 10 }}><Text strong>标题</Text><Input value={editingRule?.title || ''} onChange={e => setEditingRule((prev: any) => ({ ...prev, title: e.target.value }))} placeholder="如：服务端口必须由用户指定" /></div>
        <div style={{ marginBottom: 10 }}><Text strong>分类</Text><Select value={editingRule?.category || 'general'} onChange={v => setEditingRule((prev: any) => ({ ...prev, category: v }))} style={{ width: '100%' }} options={[{ label: '🚢 端口/部署', value: 'ports' }, { label: '🎨 设计/美工', value: 'design' }, { label: '🤔 决策/确认', value: 'decision' }, { label: '🏗 基础设施', value: 'infra' }, { label: '📝 代码风格', value: 'code' }, { label: '🔐 安全', value: 'security' }, { label: '📦 依赖', value: 'deps' }, { label: '✅ 交付', value: 'deploy' }, { label: '📌 通用', value: 'general' }]} /></div>
        <div><Text strong>内容</Text><div className="sim-modal-voice-input"><TextArea value={editingRule?.content || ''} onChange={e => setEditingRule((prev: any) => ({ ...prev, content: e.target.value }))} placeholder="详细规则内容..." autoSize={{ minRows: 3, maxRows: 8 }} /><VoiceInputButton value={editingRule?.content || ''} onChange={(v)=>setEditingRule((prev: any) => ({ ...prev, content: v }))} size="small" /></div></div>
      </Modal>
      <Modal
        className="sim-hire-modal"
        title={null}
        open={createModalOpen}
        onOk={hireEmployee}
        onCancel={() => setCreateModalOpen(false)}
        confirmLoading={loading}
        okText="确认招聘"
        cancelText="取消"
        width={560}
      >
        <div style={{ padding: '12px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🧑‍💼</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#f8fafc', marginBottom: 6 }}>招聘数字员工</div>
            <Text type="secondary" style={{ fontSize: 13 }}>为当前项目补充专业角色，系统自动配置工作区</Text>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 6, color: '#e2e8f0' }}>岗位角色</Text>
              <Select
                value={createRole}
                onChange={setCreateRole}
                size="large"
                style={{ width: '100%' }}
                options={hireRoleOptions}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 6, color: '#e2e8f0' }}>员工姓名（留空自动生成）</Text>
              <Input value={createName} onChange={e => setCreateName(e.target.value)} placeholder="如：产品助理" size="large" />
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        className="sim-conversation-detail-modal"
        title={null}
        open={!!selectedConversation}
        onCancel={closeConversationDetail}
        footer={<Button className="sim-modal-primary" type="primary" onClick={closeConversationDetail}>关闭</Button>}
        width={conversationDetailMode === 'flow' ? 1120 : 760}
      >
        {selectedConversation && (
          <div className="sim-conv-detail-panel">
            <div className="sim-conv-detail-hero">
              <div>
                <div className="sim-conv-detail-kicker">{conversationDetailMode === 'flow' ? '委派流程图' : '本条沟通详情'}</div>
                <h3>{conversationSummary(selectedConversation)}</h3>
                <p>{new Date(selectedConversation.createdAt).toLocaleString()}</p>
              </div>
              <Tag className="sim-conv-detail-tag">{conversationDetailMode === 'flow' ? '流程图' : '详情'}</Tag>
            </div>
            <div className="sim-conv-detail-route">
              <span>{selectedConversation.fromEmployee ? employeeLabel(selectedConversation.fromEmployee) : '我'}</span>
              <b>→</b>
              <span>{selectedConversation.toEmployee ? employeeLabel(selectedConversation.toEmployee) : selectedConversation.fromEmployee ? '团队/过程记录' : '项目经理'}</span>
            </div>

            {conversationDetailMode === 'detail' && (
              <div className="office-md-content sim-conv-detail-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripCodeForChat(selectedConversation.content)}</ReactMarkdown>
              </div>
            )}

            {conversationDetailMode === 'flow' && (
              <div className="sim-flow-section sim-flow-simple-section">
                <div className="sim-flow-head">
                  <div>
                    <div className="sim-flow-kicker">简单委派链路</div>
                    <h4>从需求到委派、执行、汇报的左到右流程</h4>
                  </div>
                  <Tag className="sim-flow-count-tag">
                    {conversationFlowLoading ? '加载中' : `${conversationFlow?.nodes?.length || 0} 条真实记录`}
                  </Tag>
                </div>
                {conversationFlowLoading && (
                  <div className="sim-flow-loading">
                    <LoadingOutlined /> 正在读取真实流程数据...
                  </div>
                )}
                {!conversationFlowLoading && conversationFlowError && (
                  <div className="sim-flow-error">{conversationFlowError}</div>
                )}
                {!conversationFlowLoading && !conversationFlowError && conversationFlow && conversationFlow.nodes.length > 0 && (() => {
                  // 【2026-07-05 问题3】session（干活）节点纳入展示：后台一直有"角色实际执行"的记录，
                  // 此前被过滤掉，导致流程图看起来"委派完就汇报，没人干活"。
                  const preferredKinds = new Set(['conversation', 'delegate_item', 'confirmation', 'session']);
                  const pathNodeIds = conversationFlow.path?.nodeIds || [];
                  const pathNodes = pathNodeIds
                    .map(id => conversationFlow.nodes.find(node => node.id === id))
                    .filter(Boolean) as ConversationFlowNode[];
                  // path 只覆盖主链路，session 节点可能不在 path 里：从全量节点补齐同窗口的 session
                  const baseNodes = pathNodes.length > 0
                    ? [...pathNodes, ...conversationFlow.nodes.filter(n => n.kind === 'session' && !pathNodeIds.includes(n.id))]
                    : conversationFlow.nodes;
                  const meaningfulNodes = baseNodes.filter(node => preferredKinds.has(node.kind));
                  const flowNodes = (meaningfulNodes.length > 0 ? meaningfulNodes : (pathNodes.length > 0 ? pathNodes : conversationFlow.nodes))
                    .slice()
                    .sort((a, b) => {
                      const ta = a.time ? new Date(a.time).getTime() : 0;
                      const tb = b.time ? new Date(b.time).getTime() : 0;
                      return ta - tb;
                    });
                  const flowGroups = buildDisplayFlowGroups(flowNodes);
                  return (
                    <div className="sim-simple-flow-wrap">
                      <div
                        ref={flowScrollRef}
                        className={`sim-simple-flow-scroll ${flowDragActive ? 'is-dragging' : ''}`}
                        onPointerDown={handleFlowDragStart}
                        onPointerMove={handleFlowDragMove}
                        onPointerUp={handleFlowDragEnd}
                        onPointerCancel={handleFlowDragEnd}
                        onPointerLeave={handleFlowDragEnd}
                      >
                        <div className="sim-simple-flow-row">
                          {flowGroups.map((group, index) => {
                            const node = group.primary;
                            const groupNodeIds = [node.id, ...group.merged.map(m => m.id)];
                            const cfg = flowKindConfig(node.kind);
                            const diag = (conversationFlow.diagnostics || []).find(item => item.nodeId && groupNodeIds.includes(item.nodeId));
                            const mainText = flowCardMainText(node);
                            const subText = group.merged.length > 0 ? `派活（含${group.merged.length}条沟通往来）` : flowCardSubText(node);
                            const summaryText = flowSummaryClean(node.summary);
                            const hiddenCode = flowHasHiddenCode(node.summary);
                            return (
                              <React.Fragment key={node.id}>
                                <button type="button" className={`sim-simple-flow-card sim-flow-node-${cfg.tone} ${node.status === 'anchor' ? 'is-anchor' : ''} ${selectedFlowNodeId === node.id ? 'is-selected' : ''} ${diag ? 'has-diagnostic' : ''}`} onClick={() => handleFlowCardClick(group)}>
                                  <div className="sim-flow-card-head">
                                    <span className="sim-flow-kind">{cfg.icon} {cfg.label}</span>
                                    <span className="sim-flow-time">{compactFlowTime(node.time)}</span>
                                  </div>
                                  <div className="sim-flow-main-line">{mainText}</div>
                                  <div className="sim-flow-sub-line">{subText}<span>{flowStatusLabel(node.status)}</span></div>
                                  {summaryText && <div className="sim-flow-summary">{summaryText}</div>}
                                  {hiddenCode
                                    ? <div className="sim-flow-code-hidden">已隐藏代码/工具原文，点击卡片查看详情</div>
                                    : <div className="sim-flow-code-hidden">点击查看详情</div>}
                                  {diag && <div className={`sim-simple-flow-alert sim-flow-diag-${diag.severity}`}>{diag.title}</div>}
                                </button>
                                {index < flowGroups.length - 1 && <div className="sim-simple-flow-arrow">→</div>}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                      {(conversationFlow.diagnostics || []).length > 0 && (
                        <div className="sim-flow-diagnostics sim-flow-diagnostics-compact">
                          {(conversationFlow.diagnostics || []).map(diag => (
                            <button key={diag.id} type="button" className={`sim-flow-diag sim-flow-diag-${diag.severity}`} onClick={() => diag.nodeId && setSelectedFlowNodeId(diag.nodeId)}>
                              <b>{diag.title}</b><span>{diag.detail}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {!conversationFlowLoading && !conversationFlowError && conversationFlow && conversationFlow.nodes.length === 0 && (
                  <div className="sim-flow-empty">暂无关联流程数据。这里不会伪造流程，只展示后台真实记录。</div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 【2026-07-05 问题3】流程卡片详情：主记录+被合并的沟通记录按时间完整展示 */}
      <Modal
        title={flowDetailGroup ? `${flowKindConfig(flowDetailGroup.primary.kind).icon} ${flowCardMainText(flowDetailGroup.primary)}` : '流程详情'}
        open={!!flowDetailGroup}
        onCancel={() => setFlowDetailGroup(null)}
        footer={null}
        width={680}
        zIndex={1100}
      >
        {flowDetailGroup && (
          <div style={{ maxHeight: '62vh', overflow: 'auto' }}>
            {[flowDetailGroup.primary, ...flowDetailGroup.merged]
              .slice()
              .sort((a, b) => (a.time ? new Date(a.time).getTime() : 0) - (b.time ? new Date(b.time).getTime() : 0))
              .map(n => {
                const cfg = flowKindConfig(n.kind);
                return (
                  <div key={n.id} style={{ marginBottom: 10, padding: '10px 14px', border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8 }}>
                      <Space size={6} wrap>
                        <Tag style={{ margin: 0 }}>{cfg.icon} {cfg.label}</Tag>
                        <span style={{ fontSize: 13, fontWeight: 700 }}>{flowCardMainText(n)}</span>
                        <Tag style={{ margin: 0 }} color="blue">{flowStatusLabel(n.status)}</Tag>
                      </Space>
                      <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{n.time ? new Date(n.time).toLocaleString() : ''}</Text>
                    </div>
                    {n.summary ? (
                      <div style={{ fontSize: 12, lineHeight: 1.7, wordBreak: 'break-word' }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{n.summary}</ReactMarkdown>
                      </div>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>（这条记录没有正文）</Text>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </Modal>

      <Modal title="工作历史记录" open={historyOpen} onCancel={() => setHistoryOpen(false)} width={800} footer={null}>
        <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
          {historyData.length === 0 && <Empty description="暂无记录" />}
          {historyData.map((msg, i) => {
            const fc = msg.fromEmployee ? ROLE_CONFIG[msg.fromEmployee.role] : null;
            const tc = msg.toEmployee ? ROLE_CONFIG[msg.toEmployee.role] : null;
            return <div key={msg.id} style={{ marginBottom: 8, padding: '10px 14px', borderRadius: 8, border: '1px solid #f0e8d5', background: fc ? (fc.hexColor + '10') : '#faf7ed' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Space size={4}>
                  {msg.fromEmployee && <Tag color={fc?.color} style={{ margin: 0, fontSize: 11 }}>{fc?.label}-{msg.fromEmployee.name}</Tag>}
                  {msg.toEmployee && <><span style={{ color: '#999', fontSize: 11 }}>→</span><Tag color={tc?.color} style={{ margin: 0, fontSize: 11 }}>{tc?.label}-{msg.toEmployee.name}</Tag></>}
                  {!msg.fromEmployee && !msg.toEmployee && <Tag style={{ margin: 0, fontSize: 11 }}>系统</Tag>}
                </Space>
                <Text style={{ fontSize: 10, color: '#999' }}>{new Date(msg.createdAt).toLocaleString()}</Text>
              </div>
              <div style={{ fontSize: 12, color: '#3e2e1a', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripCodeForChat(msg.content)}</ReactMarkdown>
              </div>
            </div>;
          })}
        </div>
      </Modal>
      <Modal title="招聘确认" open={!!suggestHire} onCancel={() => setSuggestHire(null)}
        onOk={async () => {
          if (!suggestHire) return;
          const hiredRoles = [...suggestHire.roles];
          for (const role of suggestHire.roles) {
            try { await api.post('/digital-employees/employees', { projectId: suggestHire.projectId, role }); } catch {}
          }
          let latestEmployees = employees;
          try { const { data } = await api.get(`/digital-employees/employees?projectId=${suggestHire.projectId}`); latestEmployees = data; setEmployees(data); } catch {}
          try {
            const pm = findProjectManager(latestEmployees);
            if (pm) {
              await api.post(`/digital-employees/projects/${suggestHire.projectId}/messages`, {
                content: `已确认招聘并完成入职：${hiredRoles.join('、')}。请基于完整团队继续拆解、委派并推进项目，等待员工汇报后再决策下一步。`,
                toEmpId: pm.id,
              });
            }
          } catch (e) {
            console.warn('[DM] notify PM after hire failed:', e);
          }
          message.success(`已招聘 ${hiredRoles.length} 人，已通知项目经理继续推进`);
          setSuggestHire(null);
        }}
        okText="一键招聘" cancelText="稍后再说"
      >
        <p>{suggestHire?.empName} 建议招聘以下角色：</p>
        <Space wrap>
          {suggestHire?.roles.map(r => {
            const labels: Record<string,string> = { PM:'项目经理', PMgr:'产品经理', Architect:'架构师', UIDesigner:'UI美工', Developer:'研发人员', Tester:'测试工程师' };
            return <Tag key={r} color="blue">{labels[r] || r}</Tag>;
          })}
        </Space>
      </Modal>
      <Modal
        title={`${needUserInput?.empName || ''} ${needUserInput?.title || '需要确认'}`}
        open={!!needUserInput && !needUserInput._minimized}
        onOk={async () => {
          if (!needUserInput) return;
          if (isDeliveryReportInput(needUserInput)) return acceptDeliveryReport();
          const isPmIdle = needUserInput.type === 'pm_idle_confirm';
          // 【2026-07-03 修复】默认回复统一为"等我安排"——后端按该文案收工（设计文档 7.1）。
          // 旧默认值"等我稍微安排"不匹配时会被当成"继续推进"，导致用户想收工 PM 反而又开始委派。
          const reply = isPmIdle && !userReply.trim() ? (needUserInput.defaultAction || '等我安排') : userReply.trim();
          if (!reply) return;
          await answerNeedUserInput(reply);
          if (isPmIdle) message.success('已确认');
        }}
        onCancel={() => setNeedUserInput((prev: any) => prev ? {...prev, _minimized: true} : null)}
        okText={isDeliveryReportInput(needUserInput) ? '确认交付并结束' : (needUserInput?.type === 'pm_idle_confirm' ? (userReply.trim() ? '发送' : (needUserInput?.defaultAction || '等我安排')) : '确认回复')}
        cancelText="最小化"
        footer={isDeliveryReportInput(needUserInput) ? [
          <Button key="supplement" loading={userReplySubmitting} disabled={userReplySubmitting} onClick={requestDeliverySupplement}>补充确认并继续</Button>,
          <Button key="accept" type="primary" loading={userReplySubmitting} disabled={userReplySubmitting} danger={!!userReply.trim()} onClick={acceptDeliveryReport}>{userReply.trim() ? '已填写意见，点此将按补充确认处理' : '确认交付并结束'}</Button>,
        ] : undefined}
      >
        <div style={{ marginBottom: 12, maxHeight: 420, overflow: 'auto' }}>
          {(() => {
            // 原文 = question + raw（交付报告的 raw 里是验收证据，此前从未展示给用户）
            const raw = [String(needUserInput?.question || ''), String(needUserInput?.raw || '')]
              .filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i).join('\n\n');
            const isDelivery = isDeliveryReportInput(needUserInput);
            const headline = needUserInput?.type === 'pm_idle_confirm'
              ? '下一步您希望怎么安排？'
              : (needUserInput?.title || '需要确认');
            // 【2026-07-04 问题7】summary/原文改为 markdown 富文本渲染（标题/列表/表格/代码块），
            // 与沟通记录详情弹窗（office-md-content + ReactMarkdown）风格统一，不再纯文本堆砌。
            const summaryMd = needUserInput?.type === 'pm_idle_confirm' ? '' : String(needUserInput?.summary || '');
            const aiQuestions = Array.isArray(needUserInput?.questions) ? needUserInput.questions.filter(Boolean) : [];
            const aiOptions = String(needUserInput?.options || '').split(/[，,]/).map((x: string) => x.trim()).filter(Boolean);
            // 【2026-07-04 问题4】页面型交付物的真实外网访问地址，醒目展示（可点击）
            const accessUrls: Array<{ label?: string; url: string; verified?: boolean }> =
              Array.isArray(needUserInput?.accessUrls) ? needUserInput.accessUrls.filter((u: any) => u && u.url) : [];
            return (
              <div>
                <div style={{ padding: 14, borderRadius: 12, background: 'linear-gradient(135deg, #fff7ed 0%, #eff6ff 100%)', border: '1px solid #fdba74', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', background: '#f97316', color: '#fff', fontWeight: 900 }}>{isDelivery ? '✓' : '?'}</span>
                    <span style={{ color: '#9a3412', fontSize: 13, fontWeight: 800 }}>{isDelivery ? '项目经理请您验收本次交付' : '项目经理需要您确认'}</span>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', lineHeight: 1.5, marginBottom: 10 }}>
                    {headline}
                  </div>
                  {accessUrls.length > 0 && (
                    <div style={{ marginBottom: 10, padding: '12px 14px', borderRadius: 10, background: 'linear-gradient(135deg, #ecfdf5 0%, #eff6ff 100%)', border: '2px solid #34d399', boxShadow: '0 2px 8px rgba(52,211,153,.25)' }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: '#065f46', marginBottom: 8 }}>🌐 在线访问地址（点击打开）</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {accessUrls.map((u, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <a href={u.url} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 14, fontWeight: 800, color: '#0958d9', wordBreak: 'break-all', textDecoration: 'underline' }}>
                              {u.url}
                            </a>
                            {u.label && u.label !== '访问地址' && <Tag color="green" style={{ margin: 0 }}>{u.label}</Tag>}
                            {u.verified === false && <Tag color="orange" style={{ margin: 0 }}>探活未通过，可能需先启动服务</Tag>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {summaryMd && summaryMd !== headline && (
                    <div className="office-md-content" style={{ marginBottom: aiQuestions.length > 0 || aiOptions.length > 0 ? 10 : 0 }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{summaryMd}</ReactMarkdown>
                    </div>
                  )}
                  {aiQuestions.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {aiQuestions.map((item: string, i: number) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,.78)', border: '1px solid rgba(251,146,60,.35)' }}>
                          <span style={{ flex: '0 0 auto', minWidth: 22, height: 22, borderRadius: 11, background: '#fed7aa', color: '#9a3412', fontSize: 12, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                          <div className="office-md-content office-md-inline" style={{ flex: 1 }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{String(item)}</ReactMarkdown>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {aiOptions.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: aiQuestions.length > 0 ? 10 : 0 }}>
                      {aiOptions.map((opt: string) => <Tag key={opt} color="orange" style={{ margin: 0 }}>{opt}</Tag>)}
                    </div>
                  )}
                </div>
                {raw && raw !== summaryMd && (
                  <details open={isDelivery && !summaryMd} style={{ border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafafa', padding: '8px 10px' }}>
                    <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: 12, fontWeight: 700 }}>{isDelivery ? '查看交付报告全文' : '查看原文'}</summary>
                    <div className="office-md-content" style={{ marginTop: 8, padding: 10, borderRadius: 8, background: '#fff' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{raw}</ReactMarkdown>
                    </div>
                  </details>
                )}
              </div>
            );
          })()}
        </div>
        <div className="sim-modal-voice-input">
          <TextArea value={userReply} onChange={e => setUserReply(e.target.value)}
            placeholder={isDeliveryReportInput(needUserInput) ? '如需补充确认，请在这里填写具体问题/修改意见（点击“补充确认”时必填）；如果接受本次交付，直接点“确认交付”。' : (needUserInput?.type === 'pm_idle_confirm' ? (needUserInput?.defaultAction || '等我安排') : '直接输入你的决定或补充说明...')} autoSize={{ minRows: 3, maxRows: 8 }} />
          <VoiceInputButton value={userReply} onChange={setUserReply} size="small" />
        </div>
      </Modal>
      {/* 等待用户确认的闪烁按钮 */}
      {needUserInput && needUserInput._minimized && (
        <div onClick={() => setNeedUserInput((prev: any) => prev ? {...prev, _minimized: false} : null)}
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
            padding: '12px 24px', borderRadius: 12, cursor: 'pointer',
            background: 'linear-gradient(135deg, #f5a623, #ff6b35)',
            color: '#fff', fontWeight: 700, fontSize: 14,
            boxShadow: '0 4px 20px rgba(245,166,35,0.5)',
            animation: 'confirmPulse 1.5s infinite',
          }}>
          ⚡ {needUserInput?.empName} 需要确认 — 点击回复
        </div>
      )}
      <Modal title="团队动态" open={stateEventsOpen} onCancel={() => setStateEventsOpen(false)} width={760} footer={null}>
        <div style={{ maxHeight: '58vh', overflow: 'auto' }}>
          {pendingConfirmations.length > 0 && (
            <div style={{ marginBottom: 12, padding: 12, borderRadius: 10, background: '#fff7ed', border: '1px solid #fed7aa' }}>
              <Text strong style={{ color: '#9a3412' }}>待用户确认：{pendingConfirmations.length} 项</Text>
              {pendingConfirmations.slice(0, 3).map(item => (
                <div key={item.id} style={{ marginTop: 8, fontSize: 12, color: '#7c2d12', lineHeight: 1.6 }}>
                  {(item.title || '项目经理需要确认')} · {item.pm?.name || item.pmEmployee?.name || '项目经理'}
                </div>
              ))}
            </div>
          )}
          {stateEvents.length === 0 && <Empty description="暂无团队动态" />}
          {stateEvents.map(evt => {
            const statusText = evt.toStatus ? (STATUS_CONFIG[evt.toStatus]?.label || evt.toStatus) : '';
            const color = evt.level === 'error' ? '#ef4444' : evt.level === 'warning' ? '#f59e0b' : '#1677ff';
            return (
              <div key={evt.id} style={{ marginBottom: 8, padding: '10px 12px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                  <Text strong style={{ color }}>{evt.employee?.name || evt.actorType || '系统'} · {evt.event}</Text>
                  <Text style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(evt.createdAt).toLocaleString()}</Text>
                </div>
                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {statusText ? `状态：${statusText}` : ''}{evt.message ? `${statusText ? ' · ' : ''}${evt.message}` : ''}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>
      <Modal title={fileViewer?.path} open={!!fileViewer} onCancel={() => setFileViewer(null)} width={800} footer={null}>
        <pre style={{ maxHeight: '70vh', overflow: 'auto', background: '#1a1a2e', color: '#e0e0e0', padding: 16, borderRadius: 8, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {fileViewer?.content}
        </pre>
      </Modal>
    </div>
  );
}
